import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { 
  Users, UserPlus, Search, RefreshCw, Loader2, Plus, Eye, Edit, 
  Trash2, CheckCircle2, XCircle, Clock, FileText, Download, Upload,
  ChevronDown, ChevronUp, Calendar, Phone, MapPin, CreditCard, File,
  Image, FileUp, History, MessageSquare, MoreVertical, Building2, Mail
} from 'lucide-react';
import { WhatsAppIcon } from '../components/WhatsAppButton';
import { formatDateDisplay, truncateName } from '../utils/dateUtils';
import useDeviceDetection from '../hooks/useDeviceDetection';
import { usePermissions } from '../utils/permissionUtils';
import { userCan } from '../utils/permissionUtils';
import { formatErrorMessage } from '../utils/formatError';

const API = process.env.REACT_APP_BACKEND_URL ? process.env.REACT_APP_BACKEND_URL + '/api' : '/api';

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
};

const NATIONALITIES = [
  "Afghanistan", "Bangladesh", "Egypt", "Ethiopia", "Philippines", "India", 
  "Indonesia", "Kenya", "Nepal", "Nigeria", "Pakistan", "Sri Lanka", 
  "Sudan", "Uganda", "Vietnam", "Other"
];

const Hiring = ({ user }) => {
  const { isMobile, isTablet } = useDeviceDetection();
  
  // Permissions - with safety checks
  const permissions = usePermissions(user, 'hiring');
  const isAdmin = user?.role === 'Super Admin' || user?.role === 'Admin';
  const canRunDataRepair = userCan(user, 'adminTools', 'edit', 'dataRepair');
  // Sequence guard against stale-response races. When the user/perms object
  // changes (e.g. permissions heartbeat refresh) a new fetchData reference is
  // created and called. We tag every in-flight request with a sequence id and
  // ignore any response whose seq is not the latest.
  const fetchSeqRef = useRef(0);
  
  // Main permissions
  const canCreate = permissions?.canCreate || isAdmin;
  const canEdit = permissions?.canEdit || isAdmin;
  const canDelete = permissions?.canDelete || isAdmin;
  
  // Document permissions - granular per document category (with fallbacks for admins)
  // Uploaded Documents
  const canUploadUploaded = permissions?.canUploadSubPage?.('uploadedDocuments') ?? user?.permissions?.hiring?.subPages?.uploadedDocuments?.upload ?? isAdmin;
  const canViewUploaded = permissions?.canViewSubPage?.('uploadedDocuments') ?? user?.permissions?.hiring?.subPages?.uploadedDocuments?.view ?? isAdmin;
  const canDownloadUploaded = permissions?.canDownloadSubPage?.('uploadedDocuments') ?? user?.permissions?.hiring?.subPages?.uploadedDocuments?.download ?? isAdmin;
  const canReplaceUploaded = permissions?.canReplaceSubPage?.('uploadedDocuments') ?? user?.permissions?.hiring?.subPages?.uploadedDocuments?.replace ?? isAdmin;
  
  // Generated Documents
  const canUploadGenerated = permissions?.canUploadSubPage?.('generatedDocuments') ?? user?.permissions?.hiring?.subPages?.generatedDocuments?.upload ?? isAdmin;
  const canViewGenerated = permissions?.canViewSubPage?.('generatedDocuments') ?? user?.permissions?.hiring?.subPages?.generatedDocuments?.view ?? isAdmin;
  const canDownloadGenerated = permissions?.canDownloadSubPage?.('generatedDocuments') ?? user?.permissions?.hiring?.subPages?.generatedDocuments?.download ?? isAdmin;
  const canReplaceGenerated = permissions?.canReplaceSubPage?.('generatedDocuments') ?? user?.permissions?.hiring?.subPages?.generatedDocuments?.replace ?? isAdmin;
  
  // Visa Documents
  const canUploadVisa = permissions?.canUploadSubPage?.('visaDocuments') ?? user?.permissions?.hiring?.subPages?.visaDocuments?.upload ?? isAdmin;
  const canViewVisa = permissions?.canViewSubPage?.('visaDocuments') ?? user?.permissions?.hiring?.subPages?.visaDocuments?.view ?? isAdmin;
  const canDownloadVisa = permissions?.canDownloadSubPage?.('visaDocuments') ?? user?.permissions?.hiring?.subPages?.visaDocuments?.download ?? isAdmin;
  const canReplaceVisa = permissions?.canReplaceSubPage?.('visaDocuments') ?? user?.permissions?.hiring?.subPages?.visaDocuments?.replace ?? isAdmin;
  
  // Travel Documents
  const canUploadTravel = permissions?.canUploadSubPage?.('travelDocuments') ?? user?.permissions?.hiring?.subPages?.travelDocuments?.upload ?? isAdmin;
  const canViewTravel = permissions?.canViewSubPage?.('travelDocuments') ?? user?.permissions?.hiring?.subPages?.travelDocuments?.view ?? isAdmin;
  const canDownloadTravel = permissions?.canDownloadSubPage?.('travelDocuments') ?? user?.permissions?.hiring?.subPages?.travelDocuments?.download ?? isAdmin;
  const canReplaceTravel = permissions?.canReplaceSubPage?.('travelDocuments') ?? user?.permissions?.hiring?.subPages?.travelDocuments?.replace ?? isAdmin;
  
  // Legacy permission aliases for backward compatibility
  const canViewDocuments = canViewUploaded || canViewGenerated || canViewVisa || canViewTravel;
  const canUploadDocuments = canUploadUploaded || canUploadGenerated || canUploadVisa || canUploadTravel;
  
  const canViewAdvancePayments = permissions?.canViewSubPage?.('advancePayments') ?? isAdmin;
  const canAddAdvancePayments = permissions?.canCreateSubPage?.('advancePayments') ?? isAdmin;
  const canEditAdvancePayments = permissions?.canEditSubPage?.('advancePayments') ?? isAdmin;
  const canDeleteAdvancePayments = permissions?.canDeleteSubPage?.('advancePayments') ?? isAdmin;
  const canApprove = permissions?.canEditSubPage?.('approvals') ?? isAdmin;
  
  // Loan Contract permissions
  const canViewLoanContract = permissions?.canViewSubPage?.('loanContract') ?? user?.permissions?.hiring?.subPages?.loanContract?.view ?? isAdmin;
  const canEditLoanContract = permissions?.canEditSubPage?.('loanContract') ?? user?.permissions?.hiring?.subPages?.loanContract?.edit ?? isAdmin;
  
  // Change Lessee permission
  const canChangeLessee = permissions?.canEditSubPage?.('changeLessee') ?? user?.permissions?.hiring?.subPages?.changeLessee?.edit ?? isAdmin;
  
  const [loading, setLoading] = useState(true);
  const [candidates, setCandidates] = useState([]);
  const [currentStep, setCurrentStep] = useState(1); // For step-wise form
  const [stats, setStats] = useState({ total_enrolled: 0, under_review: 0, sent_for_edit: 0, accepted: 0, rejected: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showDocumentsDialog, setShowDocumentsDialog] = useState(false);
  const [showGenerateDocsDialog, setShowGenerateDocsDialog] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [generatingDoc, setGeneratingDoc] = useState(null); // Track which doc is being generated
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedReason, setSelectedReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [showDateTimeDialog, setShowDateTimeDialog] = useState(false);
  const [selectedCandidateDateTime, setSelectedCandidateDateTime] = useState(null);
  const [showLoanContractDialog, setShowLoanContractDialog] = useState(false);
  const [loanContractForm, setLoanContractForm] = useState({ loan_contract_amount: 0, no_of_installments: 0 });
  const [templateAvailability, setTemplateAvailability] = useState({});
  const [showAcceptLesseeDialog, setShowAcceptLesseeDialog] = useState(false);
  const [acceptCandidateId, setAcceptCandidateId] = useState(null);
  const [selectedLesseeId, setSelectedLesseeId] = useState('');
  const [inHouseLessees, setInHouseLessees] = useState([]);
  const [showChangeLesseeDialog, setShowChangeLesseeDialog] = useState(false);

  // Predefined reasons for Reject and Send for Edit
  const REJECT_REASONS = [
    'Not fit for the job profile',
    'Candidate is under age (Less than 21 years old)',
    'Candidate is over age (More than 35 years old)',
    'Rejected due to health reasons',
    'Visa not available for this nationality',
    'Home country driving license not available',
    'Documents provided are expired or not as per requirements',
    'Other reason'
  ];
  
  const EDIT_REASONS = [
    'Resubmit high resolution scanned passport front page',
    'Resubmit high resolution scanned passport back page',
    'Resubmit high resolution scanned passport cover',
    'High resolution digital photo with white background required',
    'Resubmit Home country license',
    'Candidate information is not filled correctly',
    'Two references are required',
    'Proof of advance payment not uploaded',
    'Advance payment not deposited',
    'Partial advance received, balance advance required to proceed',
    'Other reason'
  ];

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    date_of_birth: '',
    nationality: '',
    passport_number: '',
    passport_issue_date: '',
    passport_expiry_date: '',
    state: '',
    district: '',
    town_village: '',
    full_home_address: '',
    country_code: '+971',
    mobile_no: '',
    email: '',
    lessee_company_id: '',
    lessee_company_name: '',
    education_qualification: '',
    education_certificate_file: null,
    education_certificate_url: '',
    immigration_status: '',
    police_clearance_status: '',
    police_clearance_certificate_file: null,
    police_clearance_certificate_url: '',
    reference_1_name: '',
    reference_1_mobile: '',
    reference_1_address: '',
    reference_2_name: '',
    reference_2_mobile: '',
    reference_2_address: '',
    home_country_dl_number: '',
    home_country_dl_issue_date: '',
    home_country_dl_expiry_date: '',
    advance_paid: 0,
    advance_currency: 'AED',
    advance_paid_aed: 0
  });

  // Document uploads
  const [documents, setDocuments] = useState({
    passport_front: null,
    passport_back: null,
    passport_cover: null,
    photo: null,
    driving_license: null,
    driving_license_back: null,
    id_card: null,
    id_card_back: null,
    signed_mol: null,
    // Visa Documents (Admin uploads)
    mol: null,
    employment_visa: null,
    // Travel Documents (User uploads)
    air_ticket: null
  });

  // Additional Documents — multi-file field with append-not-replace semantics.
  // Uploads immediately on Add and via a dedicated endpoint on Edit so the
  // candidate can accumulate arbitrary supporting files (contracts, letters,
  // scans, etc.) that are individually viewable / downloadable / archivable.
  const [additionalDocsToUpload, setAdditionalDocsToUpload] = useState([]);
  const [uploadingAdditional, setUploadingAdditional] = useState(false);
  
  // Advance proof upload
  const [advanceProof, setAdvanceProof] = useState(null);
  
  // New advance payment state (for edit mode)
  const [newAdvance, setNewAdvance] = useState({ amount: 0, currency: 'AED', proof: null, amount_aed: 0 });
  const [addingAdvance, setAddingAdvance] = useState(false);
  const [editingAdvanceIdx, setEditingAdvanceIdx] = useState(null);
  const [editingAdvanceAed, setEditingAdvanceAed] = useState(0);
  // Full-row edit state (currency, amount, amount_aed) — separate from the
  // legacy "Add AED" inline editor which only touched amount_aed.
  const [editingRowIdx, setEditingRowIdx] = useState(null);
  const [editingRow, setEditingRow] = useState({ currency: 'AED', amount: 0, amount_aed: 0 });
  const [savingRow, setSavingRow] = useState(false);
  const [deletingRowIdx, setDeletingRowIdx] = useState(null);
  const [uploadingProofIdx, setUploadingProofIdx] = useState(null);

  const handleUploadAdvanceProof = async (paymentIdx, file) => {
    if (!selectedCandidate || !file) return;
    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!allowed.includes(file.type)) {
      toast.error('Only JPEG, PNG or PDF files are allowed');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large — maximum size is 10 MB');
      return;
    }
    try {
      setUploadingProofIdx(paymentIdx);
      const form = new FormData();
      form.append('proof', file);
      form.append('uploaded_by_name', user?.name || user?.email || '');
      await axios.post(
        `${API}/hiring/candidates/${selectedCandidate.id}/advance-payments/${paymentIdx}/proof`,
        form,
        { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Proof uploaded successfully');
      // Refresh selected candidate so the row re-renders with the new proof
      const updated = await axios.get(
        `${API}/hiring/candidates/${selectedCandidate.id}`,
        { headers: getAuthHeader() }
      );
      setSelectedCandidate(updated.data);
      fetchData();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to upload proof'));
    } finally {
      setUploadingProofIdx(null);
    }
  };

  const handleUpdateAdvanceAed = async (paymentIdx) => {
    if (!selectedCandidate || editingAdvanceAed <= 0) {
      toast.error('Please enter a valid AED amount');
      return;
    }
    try {
      await axios.post(`${API}/hiring/candidates/${selectedCandidate.id}/advance-payments/${paymentIdx}/update-aed`,
        { amount_aed: editingAdvanceAed },
        { headers: getAuthHeader() }
      );
      toast.success('AED equivalent updated');
      setEditingAdvanceIdx(null);
      fetchData();
    } catch (error) {
      console.error('Update AED error:', error);
      toast.error('Failed to update AED equivalent');
    }
  };

  // Start editing an entire advance-payment row (currency + amount + amount_aed).
  const startEditingAdvanceRow = (idx, payment) => {
    setEditingRowIdx(idx);
    setEditingRow({
      currency: payment.currency || 'AED',
      amount: Number(payment.amount) || 0,
      amount_aed: Number(payment.amount_aed || payment.amount) || 0,
    });
  };
  const cancelEditingAdvanceRow = () => { setEditingRowIdx(null); };
  const handleSaveAdvanceRow = async (paymentIdx) => {
    if (!selectedCandidate) return;
    if (!(Number(editingRow.amount) > 0)) { toast.error('Amount must be greater than 0'); return; }
    setSavingRow(true);
    try {
      const body = {
        currency: editingRow.currency,
        amount: Number(editingRow.amount),
      };
      if (editingRow.currency !== 'AED') {
        body.amount_aed = Number(editingRow.amount_aed) || 0;
      }
      await axios.put(
        `${API}/hiring/candidates/${selectedCandidate.id}/advance-payments/${paymentIdx}`,
        body,
        { headers: getAuthHeader() }
      );
      toast.success('Advance payment updated');
      setEditingRowIdx(null);
      await fetchData();
    } catch (error) {
      console.error('Edit advance payment error:', error);
      toast.error(error?.response?.data?.detail || 'Failed to update advance payment');
    } finally {
      setSavingRow(false);
    }
  };
  const handleDeleteAdvancePayment = async (paymentIdx) => {
    if (!selectedCandidate) return;
    if (!window.confirm('Delete this advance payment entry? This cannot be undone.')) return;
    setDeletingRowIdx(paymentIdx);
    try {
      await axios.delete(
        `${API}/hiring/candidates/${selectedCandidate.id}/advance-payments/${paymentIdx}`,
        { headers: getAuthHeader() }
      );
      toast.success('Advance payment deleted');
      await fetchData();
    } catch (error) {
      console.error('Delete advance payment error:', error);
      toast.error(error?.response?.data?.detail || 'Failed to delete advance payment');
    } finally {
      setDeletingRowIdx(null);
    }
  };
  
  // Sync documents state
  const [syncingDocuments, setSyncingDocuments] = useState(false);
  const [reuploadingDocuments, setReuploadingDocuments] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);

  const isCreator = (candidate) => candidate?.created_by === user?.email;

  // Download blank candidate enrollment form (.docx) — share with new applicants.
  const handleDownloadCandidateTemplate = async () => {
    if (downloadingTemplate) return;
    setDownloadingTemplate(true);
    try {
      const res = await axios.get(`${API}/hiring/candidate-form-template`, {
        headers: getAuthHeader(),
        responseType: 'blob',
      });
      // Try to read filename from Content-Disposition; fall back to a sensible default.
      let filename = 'EzyMobility_Candidate_Form.docx';
      const cd = res.headers['content-disposition'];
      if (cd) {
        const m = cd.match(/filename="?([^";]+)"?/i);
        if (m && m[1]) filename = m[1];
      }
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Candidate form downloaded');
    } catch (error) {
      console.error('Download template error:', error);
      toast.error('Failed to download candidate form. Please try again.');
    } finally {
      setDownloadingTemplate(false);
    }
  };
  
  // Sync documents from OneDrive (Admin only) - now runs in background
  const handleSyncDocuments = async () => {
    if (!canRunDataRepair) return;
    setSyncingDocuments(true);
    try {
      await axios.post(`${API}/hiring/admin/sync-documents`, {}, { headers: getAuthHeader() });
      toast.success('Sync started in background. This may take a few minutes...', { duration: 5000 });
      // Poll for completion
      const pollInterval = setInterval(async () => {
        try {
          const statusRes = await axios.get(`${API}/hiring/admin/sync-status`, { headers: getAuthHeader() });
          if (statusRes.data.status === 'done') {
            clearInterval(pollInterval);
            setSyncingDocuments(false);
            const r = statusRes.data.result || {};
            toast.success(`Sync Complete: ${r.documents_synced || 0} synced, ${r.documents_already_local || 0} already local, ${r.documents_failed || 0} failed`, { duration: 8000 });
            fetchData();
          }
        } catch { /* keep polling */ }
      }, 5000);
      // Safety timeout: stop polling after 5 minutes
      setTimeout(() => { setSyncingDocuments(false); }, 300000);
    } catch (error) {
      console.error('Sync error:', error);
      toast.error(formatErrorMessage(error, 'Failed to start sync'));
      setSyncingDocuments(false);
    }
  };

  // Re-upload documents to OneDrive with correct paths (Admin only - one-time fix)
  const handleReuploadDocuments = async () => {
    if (!canRunDataRepair) return;
    if (!window.confirm('This will re-upload all local documents to OneDrive with correct per-candidate paths. Continue?')) return;
    setReuploadingDocuments(true);
    try {
      const response = await axios.post(`${API}/hiring/admin/reupload-documents-to-onedrive`, {}, { headers: getAuthHeader() });
      const data = response.data;
      toast.success(
        `Re-upload Complete: ${data.documents_reuploaded} re-uploaded, ${data.documents_skipped} skipped, ${data.documents_failed} failed`,
        { duration: 8000 }
      );
      fetchData();
    } catch (error) {
      console.error('Re-upload error:', error);
      toast.error(formatErrorMessage(error, 'Failed to re-upload documents'));
    } finally {
      setReuploadingDocuments(false);
    }
  };
  
  // Helper function to view document inline (uses download endpoint with inline=true)
  const viewDocument = (candidateId, docKey, docType = 'documents') => {
    let url;
    if (docType === 'documents') {
      url = `${API}/hiring/candidates/${candidateId}/documents/${docKey}/download?inline=true`;
    } else if (docType === 'advance-payment') {
      url = `${API}/hiring/candidates/${candidateId}/advance-payments/${docKey}/proof/download?inline=true`;
    } else if (docType === 'generated') {
      url = `${API}/hiring/candidates/${candidateId}/generated/${docKey}/download?inline=true`;
    }
    window.open(url, '_blank');
  };
  
  // Country codes
  const COUNTRY_CODES = [
    { code: '+91', country: 'India' },
    { code: '+971', country: 'UAE' },
    { code: '+94', country: 'Sri Lanka' },
    { code: '+977', country: 'Nepal' },
    { code: '+20', country: 'Egypt' },
    { code: '+243', country: 'DRC' }
  ];
  
  // Currencies
  const CURRENCIES = [
    { code: 'AED', name: 'UAE Dirham' },
    { code: 'INR', name: 'Indian Rupee' },
    { code: 'LKR', name: 'Sri Lankan Rupee' },
    { code: 'NPR', name: 'Nepalese Rupee' },
    { code: 'EGP', name: 'Egyptian Pound' },
    { code: 'CDF', name: 'Congolese Franc' }
  ];

  // Extract viewOwnOnly value for stable dependency (with safety)
  const viewOwnOnly = permissions?.viewOwnOnly || false;
  
  const fetchData = useCallback(async () => {
    const seq = ++fetchSeqRef.current;
    // Don't fetch until we actually have a logged-in user; otherwise the
    // backend short-circuits the view_own_only filter (it requires
    // `user_email AND view_own_only` to be truthy) and returns the global
    // list — which then sticks in state and breaks scoping for Hiring Agents.
    if (!user?.email) {
      // eslint-disable-next-line no-console
      console.log('[Hiring] skip fetch — user.email not loaded yet. seq=', seq);
      return;
    }
    try {
      setLoading(true);
      const userEmail = user.email;
      const adminFlag = isAdmin ? 'true' : 'false';
      const viewOwnOnlyFlag = viewOwnOnly ? 'true' : 'false';
      // Lightweight diagnostic — visible in console for support sessions.
      // Logs only the effective filter (no PII beyond email).
      // eslint-disable-next-line no-console
      console.log('[Hiring] effective filter:', { userEmail, isAdmin, viewOwnOnly, seq });
      const url = `${API}/hiring/candidates?user_email=${encodeURIComponent(userEmail)}&is_admin=${adminFlag}&view_own_only=${viewOwnOnlyFlag}`;
      const statsUrl = `${API}/hiring/stats?user_email=${encodeURIComponent(userEmail)}&view_own_only=${viewOwnOnlyFlag}`;
      const [statsRes, candidatesRes] = await Promise.all([
        axios.get(statsUrl, { headers: getAuthHeader() }),
        axios.get(url, { headers: getAuthHeader() })
      ]);
      // Drop stale responses — a newer fetch has already started/finished
      if (seq !== fetchSeqRef.current) {
        // eslint-disable-next-line no-console
        console.log('[Hiring] stale response ignored. seq=', seq, 'latest=', fetchSeqRef.current);
        return;
      }
      // eslint-disable-next-line no-console
      console.log('[Hiring] response applied. seq=', seq, 'count=', (candidatesRes.data.candidates || []).length);
      setStats(statsRes.data);
      setCandidates(candidatesRes.data.candidates || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      if (seq === fetchSeqRef.current) {
        setLoading(false);
      }
    }
  }, [user, isAdmin, viewOwnOnly]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch in-house lessee companies
  const fetchInHouseLessees = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/lessees`, { headers: getAuthHeader() });
      const lessees = (res.data || []).filter(l => l.lessee_type === 'in-house');
      setInHouseLessees(lessees);
    } catch (error) {
      console.error('Error fetching lessees:', error);
    }
  }, []);

  useEffect(() => {
    fetchInHouseLessees();
  }, [fetchInHouseLessees]);

  // Handle Accept with Lessee Company confirmation
  const handleAcceptCandidate = (candidateId) => {
    setAcceptCandidateId(candidateId);
    setSelectedLesseeId('');
    setShowAcceptLesseeDialog(true);
  };

  const confirmAcceptWithLessee = async () => {
    if (!selectedLesseeId) {
      toast.error('Please select a lessee company');
      return;
    }
    const lessee = inHouseLessees.find(l => l.id === selectedLesseeId);
    try {
      const submitData = new FormData();
      submitData.append('status', 'accepted');
      submitData.append('remarks', '');
      submitData.append('lessee_company_id', selectedLesseeId);
      submitData.append('lessee_company_name', lessee?.company_name || '');
      await axios.put(`${API}/hiring/candidates/${acceptCandidateId}/status`, submitData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Candidate accepted successfully');
      setShowAcceptLesseeDialog(false);
      fetchData();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to accept candidate'));
    }
  };

  // Handle Change Lessee
  const handleChangeLessee = async () => {
    if (!selectedLesseeId || !selectedCandidate) {
      toast.error('Please select a lessee company');
      return;
    }
    const lessee = inHouseLessees.find(l => l.id === selectedLesseeId);
    try {
      const submitData = new FormData();
      submitData.append('lessee_company_id', selectedLesseeId);
      submitData.append('lessee_company_name', lessee?.company_name || '');
      await axios.put(`${API}/hiring/candidates/${selectedCandidate.id}/lessee`, submitData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Lessee company updated successfully');
      setShowChangeLesseeDialog(false);
      fetchData();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to update lessee company'));
    }
  };

  const resetForm = () => {
    setFormData({
      name: '', date_of_birth: '', nationality: '', passport_number: '',
      passport_issue_date: '', passport_expiry_date: '',
      state: '', district: '', town_village: '', full_home_address: '',
      country_code: '+971', mobile_no: '', email: '', lessee_company_id: '', lessee_company_name: '',
      education_qualification: '', education_certificate_file: null, education_certificate_url: '', immigration_status: '',
      police_clearance_status: '', police_clearance_certificate_file: null, police_clearance_certificate_url: '',
      reference_1_name: '', reference_1_mobile: '', reference_1_address: '',
      reference_2_name: '', reference_2_mobile: '', reference_2_address: '',
      home_country_dl_number: '', home_country_dl_issue_date: '', home_country_dl_expiry_date: '',
      advance_paid: 0, advance_currency: 'AED', advance_paid_aed: 0
    });
    setDocuments({
      passport_front: null, passport_back: null, passport_cover: null,
      photo: null, driving_license: null, driving_license_back: null,
      id_card: null, id_card_back: null, signed_mol: null,
      mol: null, employment_visa: null, air_ticket: null
    });
    setAdditionalDocsToUpload([]);
    setAdvanceProof(null);
    setNewAdvance({ amount: 0, currency: 'AED', proof: null });
    setEditMode(false);
    setCurrentStep(1);
  };

  const handleAddNew = () => {
    resetForm();
    setCurrentStep(1);
    setShowAddDialog(true);
  };

  const handleEdit = (candidate) => {
    setSelectedCandidate(candidate);
    setCurrentStep(1);
    setFormData({
      name: candidate.name || '',
      date_of_birth: candidate.date_of_birth || '',
      nationality: candidate.nationality || '',
      passport_number: candidate.passport_number || '',
      passport_issue_date: candidate.passport_issue_date || '',
      passport_expiry_date: candidate.passport_expiry_date || '',
      state: candidate.state || '',
      district: candidate.district || '',
      town_village: candidate.town_village || '',
      full_home_address: candidate.full_home_address || '',
      country_code: candidate.country_code || '+971',
      mobile_no: candidate.mobile_no || '',
      email: candidate.email || '',
      lessee_company_id: candidate.lessee_company_id || '',
      lessee_company_name: candidate.lessee_company_name || '',
      education_qualification: candidate.education_qualification || '',
      education_certificate_file: null,
      education_certificate_url: candidate.education_certificate_url || '',
      immigration_status: candidate.immigration_status || '',
      police_clearance_status: candidate.police_clearance_status || '',
      police_clearance_certificate_file: null,
      police_clearance_certificate_url: candidate.police_clearance_certificate_url || '',
      reference_1_name: candidate.reference_1?.name || '',
      reference_1_mobile: candidate.reference_1?.mobile || '',
      reference_1_address: candidate.reference_1?.address || '',
      reference_2_name: candidate.reference_2?.name || '',
      reference_2_mobile: candidate.reference_2?.mobile || '',
      reference_2_address: candidate.reference_2?.address || '',
      home_country_dl_number: candidate.home_country_dl_number || '',
      home_country_dl_issue_date: candidate.home_country_dl_issue_date || '',
      home_country_dl_expiry_date: candidate.home_country_dl_expiry_date || '',
      advance_paid: candidate.advance_paid || 0,
      advance_currency: candidate.advance_currency || 'AED',
      advance_paid_aed: candidate.advance_paid_aed || 0
    });
    setDocuments({
      passport_front: null, passport_back: null, passport_cover: null,
      photo: null, driving_license: null, driving_license_back: null,
      id_card: null, id_card_back: null, signed_mol: null,
      mol: null, employment_visa: null, air_ticket: null
    });
    setAdditionalDocsToUpload([]);
    setAdvanceProof(null);
    setNewAdvance({ amount: 0, currency: 'AED', proof: null });
    setEditMode(true);
    setShowAddDialog(true);
  };

  const handleViewCandidate = async (candidate) => {
    try {
      const response = await axios.get(`${API}/hiring/candidates/${candidate.id}`, { headers: getAuthHeader() });
      setSelectedCandidate(response.data);
      setShowViewDialog(true);
    } catch (error) {
      toast.error('Failed to load candidate details');
    }
  };

  const handleDeleteCandidate = async (candidate) => {
    if (!window.confirm(`Are you sure you want to delete candidate "${candidate.name}"?\n\nThis action cannot be undone and will remove all associated documents.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/hiring/candidates/${candidate.id}`, { headers: getAuthHeader() });
      toast.success('Candidate deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to delete candidate'));
    }
  };
  // Send a hiring document (uploaded or generated) via WhatsApp using the
  // hiring_document_share Meta template. Filename is used as the doc label
  // and the file itself is attached as the message's document header.
  const handleWhatsAppDocument = async (candidate, docKey, docLabel = '', source = 'uploaded') => {
    const mobile = candidate?.mobile_no
      ? `${candidate.country_code || ''}${candidate.mobile_no}`
      : '';
    if (!mobile) {
      toast.error(`Cannot send — ${candidate?.name || 'this candidate'} has no mobile number on file.`);
      return;
    }
    const label = docLabel || docKey.replace(/_/g, ' ');
    if (!window.confirm(
      `📄 Send HIRING DOCUMENT "${label}" via WhatsApp to ${candidate.name || 'the candidate'} on the mobile no. ${mobile}?\n\n` +
      `The candidate will receive the ${label} as a WhatsApp document attachment.\n\n` +
      `⚠ This is NOT a warning or a salary slip. Confirm to proceed.`
    )) return;
    try {
      const mediaUrl = source === 'generated'
        ? `${API}/hiring/candidates/${candidate.id}/generated/${docKey}/download?inline=true`
        : `${API}/hiring/candidates/${candidate.id}/documents/${docKey}/download?inline=true`;
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/whatsapp/manual-send`,
        {
          template_name: 'hiring_document_share',
          to_raw: mobile,
          body_parameters: [
            { type: 'text', text: candidate.name || 'Candidate' },
            { type: 'text', text: label },
            { type: 'text', text: '3' },
          ],
          entity_type: source === 'generated' ? 'hiring_generated_document' : 'hiring_document',
          entity_id: `${candidate.id}:${docKey}`,
          recipient_name: candidate.name,
          recipient_type: 'lessee',
          recipient_id: candidate.id,
          media_url: mediaUrl,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const s = res.data?.status;
      if (s === 'sent' || s === 'delivered') toast.success(`WhatsApp queued to ${candidate.name}`);
      else if (s === 'skipped') toast.warning(`Skipped — ${res.data?.reason || 'not opted-in'}`);
      else toast.error(`Failed — ${res.data?.error || 'unknown'}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'WhatsApp send failed');
    }
  };



  const handleEmailDocument = async (candidate, docType, source = 'generated', docLabel = '') => {
    if (!candidate?.email || candidate.email.trim() === '') {
      toast.error('This candidate has no email address. Please edit the candidate and add a valid email first.');
      return;
    }
    if (!window.confirm(`Send "${docLabel || docType}" as PDF to ${candidate.email}?`)) return;
    try {
      const fd = new FormData();
      fd.append('document_type', docType);
      fd.append('source', source);
      const res = await axios.post(
        `${API}/hiring/candidates/${candidate.id}/email-document`,
        fd,
        { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(res.data?.message || 'Email sent successfully');
    } catch (err) {
      console.error('email-document failed', err);
      toast.error(err?.response?.data?.detail || 'Failed to email document');
    }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.passport_number || !formData.nationality || !formData.date_of_birth) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (!formData.education_qualification) {
      toast.error('Educational Qualification is required');
      return;
    }
    const _emailVal = (formData.email || '').trim();
    if (!_emailVal || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(_emailVal)) {
      toast.error('Please provide a valid email address for the candidate');
      return;
    }
    if (formData.advance_currency !== 'AED' && formData.advance_paid > 0 && !formData.advance_paid_aed) {
      toast.error('Equivalent AED amount is required for non-AED currency');
      return;
    }

    try {
      setSaving(true);
      const submitData = new FormData();
      
      Object.keys(formData).forEach(key => {
        // Files & server URLs are appended separately / skipped below.
        if (key === 'education_certificate_file' || key === 'education_certificate_url' ||
            key === 'police_clearance_certificate_file' || key === 'police_clearance_certificate_url') return;
        if (formData[key] !== null && formData[key] !== undefined) {
          submitData.append(key, formData[key]);
        }
      });

      // Optional education certificate upload
      if (formData.education_certificate_file) {
        submitData.append('education_certificate', formData.education_certificate_file);
      }
      // Optional police clearance certificate upload
      if (formData.police_clearance_certificate_file) {
        submitData.append('police_clearance_certificate', formData.police_clearance_certificate_file);
      }
      
      // Add creator info
      submitData.append('created_by_name', user?.name || user?.email || '');
      submitData.append('created_by_email', user?.email || '');

      Object.keys(documents).forEach(key => {
        if (documents[key]) {
          submitData.append(key, documents[key]);
        }
      });
      
      // Add advance proof
      if (advanceProof) {
        submitData.append('advance_proof', advanceProof);
      }

      if (editMode && selectedCandidate) {
        submitData.append('updated_by_name', user?.name || user?.email || '');
        await axios.put(`${API}/hiring/candidates/${selectedCandidate.id}`, submitData, {
          headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Candidate updated successfully');
      } else {
        const createRes = await axios.post(`${API}/hiring/candidates`, submitData, {
          headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
        });
        // If Additional Documents were queued during Add flow, upload them
        // immediately to the newly created candidate id (append endpoint).
        const newId = createRes.data?.candidate?.id || createRes.data?.id;
        if (newId && additionalDocsToUpload.length > 0) {
          try {
            const fd = new FormData();
            additionalDocsToUpload.forEach(f => fd.append('files', f));
            await axios.post(
              `${API}/hiring/candidates/${newId}/additional-documents`,
              fd,
              { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
            );
          } catch (adErr) {
            console.error('Additional documents upload failed:', adErr);
            toast.warning('Candidate created, but additional documents failed to upload — please retry from Edit');
          }
        }
        toast.success('Candidate enrolled successfully');
      }

      setShowAddDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving candidate:', error);
      // Extract detailed error message
      let errorMsg = 'Failed to save candidate';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          // FastAPI 422 validation error
          errorMsg = detail.map(e => `${e.loc?.join('.')||''}: ${e.msg}`).join(', ');
        } else {
          errorMsg = String(detail);
        }
      } else if (error.response?.status) {
        errorMsg = `Server error (${error.response.status}): ${error.response.statusText || 'Unknown error'}`;
      } else if (error.message) {
        errorMsg = error.message;
      }
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };
  
  // Add new advance payment
  const handleAddAdvance = async () => {
    if (!selectedCandidate || newAdvance.amount <= 0) {
      toast.error('Please enter a valid advance amount');
      return;
    }
    if (newAdvance.currency !== 'AED' && !newAdvance.amount_aed) {
      toast.error('Equivalent AED amount is required for non-AED currency');
      return;
    }
    
    try {
      setAddingAdvance(true);
      const submitData = new FormData();
      submitData.append('amount', newAdvance.amount);
      submitData.append('currency', newAdvance.currency);
      submitData.append('added_by_name', user?.name || user?.email || '');
      if (newAdvance.currency !== 'AED' && newAdvance.amount_aed > 0) {
        submitData.append('amount_aed', newAdvance.amount_aed);
      }
      
      if (newAdvance.proof) {
        submitData.append('proof', newAdvance.proof);
      }
      
      const response = await axios.post(`${API}/hiring/candidates/${selectedCandidate.id}/advance`, submitData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Advance payment added successfully');
      setNewAdvance({ amount: 0, currency: 'AED', proof: null, amount_aed: 0 });
      
      // Refresh candidate data
      const updatedCandidate = await axios.get(`${API}/hiring/candidates/${selectedCandidate.id}`, { headers: getAuthHeader() });
      setSelectedCandidate(updatedCandidate.data);
      fetchData();
    } catch (error) {
      console.error('Error adding advance:', error);
      toast.error('Failed to add advance payment');
    } finally {
      setAddingAdvance(false);
    }
  };

  const handleStatusChange = async (candidateId, newStatus, remarks = '') => {
    try {
      const submitData = new FormData();
      submitData.append('status', newStatus);
      submitData.append('remarks', remarks);

      await axios.put(`${API}/hiring/candidates/${candidateId}/status`, submitData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Status updated to ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleGenerateDocument = async (candidateId, docType, loanData = null) => {
    // For loan_agreement, check permission and show the loan contract popup first
    if (docType === 'loan_agreement' && !loanData) {
      if (!canEditLoanContract) {
        toast.error('You do not have permission to manage loan contract details');
        return;
      }
      // Pre-fill from candidate's existing data if available
      const candidate = candidates.find(c => c.id === candidateId);
      setLoanContractForm({
        loan_contract_amount: candidate?.loan_contract_amount || 0,
        no_of_installments: candidate?.no_of_installments || 0
      });
      setShowLoanContractDialog(true);
      return;
    }

    setGeneratingDoc(docType);
    try {
      const submitData = new FormData();
      submitData.append('document_type', docType);

      // Pass loan contract data as custom_field_values for loan_agreement
      if (docType === 'loan_agreement' && loanData) {
        const installment = loanData.loan_contract_amount > 0 && loanData.no_of_installments > 0
          ? (loanData.loan_contract_amount / loanData.no_of_installments).toFixed(2) : '0.00';
        submitData.append('custom_field_values', JSON.stringify({
          loan_contract_amount: String(loanData.loan_contract_amount),
          no_of_installments: String(loanData.no_of_installments),
          installment_amount: installment
        }));

        // Save loan contract values to the candidate record
        try {
          await axios.put(`${API}/hiring/candidates/${candidateId}/loan-contract`, {
            loan_contract_amount: loanData.loan_contract_amount,
            no_of_installments: loanData.no_of_installments
          }, { headers: getAuthHeader() });
        } catch (saveErr) {
          console.error('Failed to save loan contract to candidate:', saveErr);
        }
      }

      const response = await axios.post(`${API}/hiring/candidates/${candidateId}/generate-document`, submitData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`${docType.replace(/_/g, ' ')} generated successfully`);
      viewDocument(candidateId, docType, 'generated');
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const status = error.response?.status;
      let errorMsg = 'Failed to generate document';
      if (detail) {
        errorMsg = detail;
      } else if (status === 404) {
        errorMsg = `No template found for ${docType.replace(/_/g, ' ')}. Please upload a template first.`;
      } else if (status === 400) {
        errorMsg = `Cannot generate document: ${detail || 'Invalid request'}`;
      } else if (!error.response) {
        errorMsg = 'Network error. Please check your connection and try again.';
      }
      toast.error(errorMsg);
      console.error(`Document generation error (${docType}):`, error.response?.data || error.message);
    } finally {
      setGeneratingDoc(null);
    }
  };

  const handleUploadSigned = async (candidateId, docType, file) => {
    try {
      const submitData = new FormData();
      submitData.append('document_type', docType);
      submitData.append('signed_file', file);

      await axios.post(`${API}/hiring/candidates/${candidateId}/upload-signed`, submitData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Signed document uploaded successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to upload signed document');
    }
  };

  const filteredCandidates = candidates.filter(c => {
    const matchesSearch = searchTerm === '' || 
      c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.passport_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.nationality?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || c.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const base = "text-[10px] px-1.5 py-0.5 font-medium";
    switch (status) {
      case 'accepted':
        return <Badge className={`${base} bg-green-100 text-green-700 border-green-300`}><CheckCircle2 className="w-2.5 h-2.5 mr-0.5" />Accepted</Badge>;
      case 'rejected':
        return <Badge className={`${base} bg-red-100 text-red-700 border-red-300`}><XCircle className="w-2.5 h-2.5 mr-0.5" />Rejected</Badge>;
      case 'sent_for_edit':
        return <Badge className={`${base} bg-orange-100 text-orange-700 border-orange-300`}><Edit className="w-2.5 h-2.5 mr-0.5" />Edit</Badge>;
      default:
        return <Badge className={`${base} bg-amber-100 text-amber-700 border-amber-300`}><Clock className="w-2.5 h-2.5 mr-0.5" />Review</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="hiring-portal">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 flex items-center gap-2">
            <Users className="w-7 h-7 text-indigo-600" />
            Hiring Portal
          </h1>
          <p className="text-sm text-slate-600 mt-1">Manage candidate enrollment and documentation</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchData} variant="outline" size="sm" className="gap-2" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
          {canRunDataRepair && (
            <Button 
              onClick={handleSyncDocuments} 
              variant="outline" 
              size="sm" 
              className="gap-2"
              disabled={syncingDocuments}
              data-testid="sync-documents-btn"
            >
              {syncingDocuments ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              {syncingDocuments ? 'Syncing...' : 'Sync Docs'}
            </Button>
          )}
          {canCreate && (
            <Button
              onClick={handleDownloadCandidateTemplate}
              variant="outline"
              size="sm"
              className="gap-2"
              disabled={downloadingTemplate}
              data-testid="download-candidate-template-btn"
              title="Download a blank candidate enrollment form (.docx) to share with new applicants"
            >
              {downloadingTemplate ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <FileText className="w-4 h-4" />
              )}
              {downloadingTemplate ? 'Preparing…' : 'Candidate Form'}
            </Button>
          )}
          {canCreate && (
            <Button onClick={handleAddNew} className="gap-2" data-testid="add-candidate-btn">
              <UserPlus className="w-4 h-4" />
              Add Candidate
            </Button>
          )}
        </div>
      </div>

      {/* Alert Banner for Candidates Needing Edit */}
      {!isAdmin && (stats.sent_for_edit || 0) > 0 && (
        <div 
          className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-center justify-between cursor-pointer hover:bg-orange-100 transition-colors"
          onClick={() => setStatusFilter('sent_for_edit')}
          data-testid="needs-edit-alert"
        >
          <div className="flex items-center gap-3">
            <div className="bg-orange-500 text-white p-2 rounded-full">
              <MessageSquare className="w-5 h-5" />
            </div>
            <div>
              <h4 className="font-semibold text-orange-800">Action Required</h4>
              <p className="text-sm text-orange-600">
                You have {stats.sent_for_edit} candidate{stats.sent_for_edit > 1 ? 's' : ''} that need{stats.sent_for_edit === 1 ? 's' : ''} revision. Click to view.
              </p>
            </div>
          </div>
          <ChevronDown className="w-5 h-5 text-orange-500" />
        </div>
      )}

      {/* Stats Dashboard */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-blue-700">{stats.total_enrolled}</div>
            <div className="text-sm text-blue-600">Total Enrolled</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-amber-700">{stats.under_review}</div>
            <div className="text-sm text-amber-600">Under Review</div>
          </CardContent>
        </Card>
        <Card 
          className={`bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200 cursor-pointer transition-all hover:shadow-md ${statusFilter === 'sent_for_edit' ? 'ring-2 ring-orange-500' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'sent_for_edit' ? 'all' : 'sent_for_edit')}
          data-testid="sent-for-edit-card"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-orange-700">{stats.sent_for_edit || 0}</div>
                <div className="text-sm text-orange-600">Needs Edit</div>
              </div>
              {(stats.sent_for_edit || 0) > 0 && (
                <div className="bg-orange-500 text-white p-2 rounded-full animate-pulse">
                  <Edit className="w-4 h-4" />
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-700">{stats.accepted}</div>
            <div className="text-sm text-green-600">Accepted</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-red-700">{stats.rejected}</div>
            <div className="text-sm text-red-600">Rejected</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name, passport, or nationality..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="under_review">Under Review</SelectItem>
                <SelectItem value="sent_for_edit">Sent for Edit</SelectItem>
                <SelectItem value="accepted">Accepted</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Candidates Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Candidates ({filteredCandidates.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="w-full">
            <Table className="table-fixed w-full">
              <TableHeader>
                <TableRow>
                  <TableHead className="text-left whitespace-normal text-xs w-[13%]">Name</TableHead>
                  <TableHead className="text-center whitespace-normal text-xs w-[7%]">Nationality</TableHead>
                  <TableHead className="text-center whitespace-normal text-xs w-[8%]">Passport No.</TableHead>
                  <TableHead className="text-center whitespace-normal text-xs w-[3%]">Age</TableHead>
                  <TableHead className="text-center whitespace-normal text-xs w-[7%]">Home Country DL No.</TableHead>
                  <TableHead className="text-center whitespace-normal text-xs w-[6%]">Date & Time</TableHead>
                  <TableHead className="text-center whitespace-normal text-xs w-[5%]">Docs</TableHead>
                  <TableHead className="text-center whitespace-normal text-xs w-[8%]">Advance</TableHead>
                  <TableHead className="text-center whitespace-normal text-xs w-[7%]">Status</TableHead>
                  <TableHead className="text-center whitespace-normal text-xs w-[9%]">Lessee Company</TableHead>
                  <TableHead className="text-center whitespace-normal text-xs w-[8%]">Contract Loan</TableHead>
                  {isAdmin && <TableHead className="text-center whitespace-normal text-xs w-[7%]">Added By</TableHead>}
                  <TableHead className="text-center whitespace-normal text-xs w-[4%]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCandidates.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isAdmin ? 11 : 10} className="text-center py-8 text-slate-500">
                      No candidates found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCandidates.map(candidate => (
                    <TableRow 
                      key={candidate.id}
                      className={`cursor-pointer transition-colors ${candidate.status === 'sent_for_edit' ? 'bg-orange-50 hover:bg-orange-100' : 'hover:bg-slate-50'}`}
                      onClick={() => {
                        setSelectedCandidate(candidate);
                        setShowViewDialog(true);
                      }}
                      data-testid={`candidate-row-${candidate.id}`}
                    >
                      <TableCell className="font-medium text-left text-xs">
                        <div className="flex items-center gap-1">
                          {candidate.name}
                          {candidate.status === 'sent_for_edit' && (
                            <span 
                              className="inline-flex items-center gap-1 text-xs text-orange-600 bg-orange-100 px-2 py-0.5 rounded-full cursor-help"
                              title={candidate.edit_reason || candidate.remarks || 'Needs revision'}
                              data-testid={`edit-required-badge-${candidate.id}`}
                            >
                              <Edit className="w-3 h-3" />
                              Edit Required
                            </span>
                          )}
                          {candidate.status === 'rejected' && candidate.reject_reason && (
                            <span 
                              className="inline-flex items-center gap-1 text-xs text-red-600 bg-red-100 px-2 py-0.5 rounded-full cursor-help"
                              title={candidate.reject_reason}
                              data-testid={`rejected-badge-${candidate.id}`}
                            >
                              Rejected
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center text-xs">{candidate.nationality}</TableCell>
                      <TableCell className="text-center font-mono text-xs">{candidate.passport_number}</TableCell>
                      <TableCell className="text-center text-xs">{candidate.age || '-'}</TableCell>
                      <TableCell className="text-center font-mono text-xs break-all">{candidate.home_country_dl_number || '-'}</TableCell>
                      <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedCandidateDateTime(candidate);
                            setShowDateTimeDialog(true);
                          }}
                          className="gap-1 text-xs h-7 px-2"
                          data-testid={`view-datetime-${candidate.id}`}
                        >
                          <Calendar className="w-3 h-3" />
                          View
                        </Button>
                      </TableCell>
                      <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-center">
                          <div className="relative inline-flex flex-col items-center">
                            {candidate.generated_documents && Object.keys(candidate.generated_documents).length > 0 && (
                              <span className="bg-green-500 text-white text-[10px] leading-none rounded-full w-4 h-4 flex items-center justify-center mb-0.5">
                                {Object.keys(candidate.generated_documents).length}
                              </span>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setSelectedCandidate(candidate);
                                setShowDocumentsDialog(true);
                              }}
                              className="gap-1 text-xs h-7 px-2"
                            >
                              <Eye className="w-3 h-3" />
                              View
                            </Button>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-center text-xs whitespace-normal break-words leading-tight">AED {candidate.advance_currency !== 'AED' && candidate.advance_paid_aed ? candidate.advance_paid_aed : (candidate.advance_paid || 0)}</TableCell>
                      <TableCell className="text-center">{getStatusBadge(candidate.status)}</TableCell>
                      <TableCell className="text-center text-xs text-slate-600" data-testid={`lessee-company-${candidate.id}`}>
                        {candidate.lessee_company_name || '-'}
                      </TableCell>
                      <TableCell className="text-center text-xs text-slate-600" data-testid={`contract-loan-${candidate.id}`}>
                        {candidate.loan_contract_amount ? `${Number(candidate.loan_contract_amount).toLocaleString()} AED` : '-'}
                      </TableCell>
                      {isAdmin && (
                        <TableCell className="text-center text-xs text-slate-600">
                          {candidate.created_by_name || candidate.created_by || '-'}
                        </TableCell>
                      )}
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-center gap-1">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-8 w-8 p-0"
                                data-testid={`actions-menu-${candidate.id}`}
                              >
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              <DropdownMenuItem 
                                onClick={() => handleViewCandidate(candidate)}
                                data-testid={`view-candidate-${candidate.id}`}
                              >
                                <Eye className="w-4 h-4 mr-2" />
                                View
                              </DropdownMenuItem>
                              {canEdit && (
                                <DropdownMenuItem 
                                  onClick={() => handleEdit(candidate)}
                                  data-testid={`edit-candidate-${candidate.id}`}
                                >
                                  <Edit className="w-4 h-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                              )}
                              {(isAdmin || canApprove) && (
                                <>
                                  <DropdownMenuSeparator />
                                  {candidate.status !== 'accepted' && (
                                    <DropdownMenuItem 
                                      onClick={() => handleAcceptCandidate(candidate.id)}
                                      className="text-green-600"
                                      data-testid={`accept-candidate-${candidate.id}`}
                                    >
                                      <CheckCircle2 className="w-4 h-4 mr-2" />
                                      Accept
                                    </DropdownMenuItem>
                                  )}
                                  {candidate.status !== 'sent_for_edit' && candidate.status !== 'accepted' && (
                                    <DropdownMenuItem 
                                      onClick={() => {
                                        setSelectedCandidate(candidate);
                                        setSelectedReason('');
                                        setCustomReason('');
                                        setShowEditDialog(true);
                                      }}
                                      className="text-orange-600"
                                      data-testid={`send-for-edit-${candidate.id}`}
                                    >
                                      <MessageSquare className="w-4 h-4 mr-2" />
                                      Send for Edit
                                    </DropdownMenuItem>
                                  )}
                                  {candidate.status !== 'rejected' && (
                                    <DropdownMenuItem 
                                      onClick={() => {
                                        setSelectedCandidate(candidate);
                                        setSelectedReason('');
                                        setCustomReason('');
                                        setShowRejectDialog(true);
                                      }}
                                      className="text-red-600"
                                      data-testid={`reject-candidate-${candidate.id}`}
                                    >
                                      <XCircle className="w-4 h-4 mr-2" />
                                      Reject
                                    </DropdownMenuItem>
                                  )}
                                </>
                              )}
                              {candidate.status === 'accepted' && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem 
                                    onClick={async () => {
                                      setSelectedCandidate(candidate);
                                      setShowGenerateDocsDialog(true);
                                      try {
                                        const res = await axios.get(`${API}/hiring/templates/availability`, { headers: getAuthHeader() });
                                        setTemplateAvailability(res.data.templates || {});
                                      } catch (e) {
                                        console.error('Failed to check template availability:', e);
                                      }
                                    }}
                                    className="text-indigo-600"
                                    data-testid={`generate-docs-${candidate.id}`}
                                  >
                                    <FileText className="w-4 h-4 mr-2" />
                                    Generate Documents
                                  </DropdownMenuItem>
                                  {candidate.generated_documents && Object.keys(candidate.generated_documents).length > 0 && (
                                    <DropdownMenuItem 
                                      onClick={() => {
                                        setSelectedCandidate(candidate);
                                        setShowDocumentsDialog(true);
                                      }}
                                      className="text-blue-600"
                                      data-testid={`view-generated-docs-${candidate.id}`}
                                    >
                                      <Download className="w-4 h-4 mr-2" />
                                      View Generated Documents
                                    </DropdownMenuItem>
                                  )}
                                  {canChangeLessee && (
                                    <DropdownMenuItem 
                                      onClick={() => {
                                        setSelectedCandidate(candidate);
                                        setSelectedLesseeId(candidate.lessee_company_id || '');
                                        setShowChangeLesseeDialog(true);
                                      }}
                                      className="text-orange-600"
                                      data-testid={`change-lessee-${candidate.id}`}
                                    >
                                      <Building2 className="w-4 h-4 mr-2" />
                                      Change Lessee
                                    </DropdownMenuItem>
                                  )}
                                </>
                              )}
                              {(isAdmin || canDelete) && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem 
                                    onClick={() => handleDeleteCandidate(candidate)}
                                    className="text-red-600 focus:text-red-600 focus:bg-red-50"
                                    data-testid={`delete-candidate-${candidate.id}`}
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Candidate Dialog */}
      <Dialog open={showAddDialog} onOpenChange={(open) => {
        if (!open) {
          setShowAddDialog(false);
          resetForm();
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editMode ? 'Edit Candidate' : 'Add New Candidate'}</DialogTitle>
            <DialogDescription>Fill in the candidate information and upload required documents</DialogDescription>
          </DialogHeader>

          {/* Admin Remarks Banner - Show prominently when status is sent_for_edit */}
          {editMode && selectedCandidate?.status === 'sent_for_edit' && selectedCandidate?.remarks && (
            <div className="bg-orange-50 border-l-4 border-orange-500 p-4 rounded-r-lg mb-4" data-testid="edit-remarks-banner">
              <div className="flex items-start gap-3">
                <MessageSquare className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-orange-800">Action Required</h4>
                  <p className="text-orange-700 mt-1">{selectedCandidate.remarks}</p>
                  <p className="text-xs text-orange-500 mt-2">Please make the requested changes and save to resubmit for review.</p>
                </div>
              </div>
            </div>
          )}

          <Tabs value={currentStep === 1 ? 'basic' : currentStep === 2 ? 'references' : 'documents'} className="w-full">
            {/* Step Indicator */}
            <div className="flex items-center justify-center gap-2 py-4 mb-4">
              {[
                { step: 1, label: 'Basic Info' },
                { step: 2, label: 'References' },
                { step: 3, label: 'Documents' }
              ].map(({ step, label }, idx) => (
                <div key={step} className="flex items-center">
                  <div
                    onClick={() => {
                      // Allow clicking to navigate between steps
                      if (step < currentStep || (step === 2 && currentStep === 1 && formData.name && formData.passport_number)) {
                        setCurrentStep(step);
                      } else if (step === currentStep + 1) {
                        setCurrentStep(step);
                      }
                    }}
                    className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium cursor-pointer transition-all ${
                      currentStep === step
                        ? 'bg-slate-900 text-white'
                        : currentStep > step
                        ? 'bg-green-500 text-white'
                        : 'bg-slate-200 text-slate-500'
                    }`}
                  >
                    {currentStep > step ? '✓' : step}
                  </div>
                  <span className={`ml-2 text-sm ${currentStep === step ? 'font-medium text-slate-900' : 'text-slate-500'}`}>
                    {label}
                  </span>
                  {idx < 2 && (
                    <div className={`w-12 h-0.5 mx-3 ${currentStep > step ? 'bg-green-500' : 'bg-slate-200'}`} />
                  )}
                </div>
              ))}
            </div>

            {/* Hide TabsList but keep Tabs for state management */}
            <TabsList className="hidden">
              <TabsTrigger value="basic">Basic Info</TabsTrigger>
              <TabsTrigger value="references">References</TabsTrigger>
              <TabsTrigger value="documents">Documents</TabsTrigger>
            </TabsList>

            <TabsContent value="basic" className="space-y-4 mt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Full Name (As per Passport) *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter full name as per passport"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date of Birth *</Label>
                  <Input
                    type="date"
                    value={formData.date_of_birth}
                    onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nationality *</Label>
                  <Select
                    value={formData.nationality}
                    onValueChange={(value) => setFormData({ ...formData, nationality: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select nationality" />
                    </SelectTrigger>
                    <SelectContent>
                      {NATIONALITIES.map(nat => (
                        <SelectItem key={nat} value={nat}>{nat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Passport Number *</Label>
                  <Input
                    value={formData.passport_number}
                    onChange={(e) => setFormData({ ...formData, passport_number: e.target.value })}
                    placeholder="Enter passport number"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Passport Issue Date</Label>
                  <Input
                    type="date"
                    value={formData.passport_issue_date}
                    onChange={(e) => setFormData({ ...formData, passport_issue_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Passport Expiry Date</Label>
                  <Input
                    type="date"
                    value={formData.passport_expiry_date}
                    onChange={(e) => setFormData({ ...formData, passport_expiry_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Mobile Number *</Label>
                  <div className="flex gap-2">
                    <Select
                      value={formData.country_code}
                      onValueChange={(value) => setFormData({ ...formData, country_code: value })}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue placeholder="Code" />
                      </SelectTrigger>
                      <SelectContent>
                        {COUNTRY_CODES.map(({ code, country }) => (
                          <SelectItem key={code} value={code}>{code} ({country})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      className="flex-1"
                      value={formData.mobile_no}
                      onChange={(e) => setFormData({ ...formData, mobile_no: e.target.value })}
                      placeholder="Enter mobile number"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Email <span className="text-red-500">*</span></Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="candidate@example.com"
                    required
                    data-testid="hiring-candidate-email-input"
                  />
                  <p className="text-xs text-slate-500">Documents (Offer Letter, Visa, etc.) will be emailed here.</p>
                </div>
                <div className="space-y-2">
                  <Label>Lessee Company</Label>
                  <Select
                    value={formData.lessee_company_id || ''}
                    onValueChange={(value) => {
                      const L = inHouseLessees.find(x => x.id === value);
                      setFormData({
                        ...formData,
                        lessee_company_id: value,
                        lessee_company_name: L?.company_name || '',
                      });
                    }}
                  >
                    <SelectTrigger data-testid="hiring-candidate-lessee-select">
                      <SelectValue placeholder="Select lessee company" />
                    </SelectTrigger>
                    <SelectContent>
                      {inHouseLessees.length === 0 && (
                        <SelectItem value="__none__" disabled>No in-house lessees available</SelectItem>
                      )}
                      {inHouseLessees.map(L => (
                        <SelectItem key={L.id} value={L.id}>{L.company_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">Documents emailed to the candidate will be branded with this company.</p>
                </div>
              </div>

              {/* Educational Qualifications */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                <div className="space-y-2">
                  <Label>Educational Qualification <span className="text-red-500">*</span></Label>
                  <Select
                    value={formData.education_qualification || ''}
                    onValueChange={(v) => setFormData({ ...formData, education_qualification: v })}
                  >
                    <SelectTrigger data-testid="hiring-edu-qual-select">
                      <SelectValue placeholder="Select qualification" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Under 10th Grade">Under 10th Grade</SelectItem>
                      <SelectItem value="Above 10th Grade">Above 10th Grade</SelectItem>
                      <SelectItem value="Graduate">Graduate</SelectItem>
                      <SelectItem value="Post Graduate">Post Graduate</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Educational Certificate <span className="text-slate-400 text-xs">(optional)</span></Label>
                  <Input
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={(e) => setFormData({ ...formData, education_certificate_file: e.target.files?.[0] || null })}
                    data-testid="hiring-edu-cert-file"
                  />
                  {formData.education_certificate_url && !formData.education_certificate_file && (
                    <p className="text-[11px] text-slate-500">Current: <a href={formData.education_certificate_url} target="_blank" rel="noreferrer" className="text-blue-600 underline">view file</a></p>
                  )}
                </div>
              </div>

              {/* Immigration Status (ECR / ECNR — used for emigration clearance) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                <div className="space-y-2">
                  <Label>Immigration Status</Label>
                  <Select
                    value={formData.immigration_status || ''}
                    onValueChange={(v) => setFormData({ ...formData, immigration_status: v })}
                  >
                    <SelectTrigger data-testid="hiring-immigration-status-select">
                      <SelectValue placeholder="Select immigration status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ECR">ECR (Emigration Check Required)</SelectItem>
                      <SelectItem value="ECNR">ECNR (Emigration Check Not Required)</SelectItem>
                      <SelectItem value="Bureau Clearance Required">Bureau Clearance Required</SelectItem>
                      <SelectItem value="Clear for Immigration">Clear for Immigration</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Police Clearance Certificate</Label>
                  <Select
                    value={formData.police_clearance_status || ''}
                    onValueChange={(v) => setFormData({ ...formData, police_clearance_status: v })}
                  >
                    <SelectTrigger data-testid="hiring-pcc-status-select">
                      <SelectValue placeholder="Select Yes / No" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Yes">Yes</SelectItem>
                      <SelectItem value="No">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                <div className="space-y-2">
                  <Label>Police Clearance Certificate File <span className="text-slate-400 text-xs">(optional)</span></Label>
                  <Input
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={(e) => setFormData({ ...formData, police_clearance_certificate_file: e.target.files?.[0] || null })}
                    data-testid="hiring-pcc-file"
                  />
                  {formData.police_clearance_certificate_url && !formData.police_clearance_certificate_file && (
                    <p className="text-[11px] text-slate-500">Current: <a href={formData.police_clearance_certificate_url} target="_blank" rel="noreferrer" className="text-blue-600 underline">view file</a></p>
                  )}
                </div>
              </div>
              
              {/* Advance Payment Section - Only show for new candidates, not in edit mode */}
              {!editMode && (
                <div className="border-t pt-4 mt-4">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <CreditCard className="w-4 h-4" />
                    Advance Payment
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Currency</Label>
                      <Select
                        value={formData.advance_currency}
                        onValueChange={(value) => setFormData({ ...formData, advance_currency: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select currency" />
                        </SelectTrigger>
                        <SelectContent>
                          {CURRENCIES.map(({ code, name }) => (
                            <SelectItem key={code} value={code}>{code} - {name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Amount</Label>
                      <Input
                        type="number"
                        value={formData.advance_paid}
                        onChange={(e) => setFormData({ ...formData, advance_paid: parseFloat(e.target.value) || 0 })}
                        placeholder="0"
                      />
                    </div>
                    {formData.advance_currency && formData.advance_currency !== 'AED' && (
                      <div className="space-y-2">
                        <Label>Equivalent AED - UAE Dirham <span className="text-red-500">*</span></Label>
                        <Input
                          type="number"
                          data-testid="advance-paid-aed"
                          required
                          value={formData.advance_paid_aed}
                          onChange={(e) => setFormData({ ...formData, advance_paid_aed: parseFloat(e.target.value) || 0 })}
                          placeholder="0"
                        />
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label>Proof of Payment</Label>
                      <Input
                        type="file"
                        accept=".jpg,.jpeg,.png,.pdf"
                        onChange={(e) => setAdvanceProof(e.target.files?.[0] || null)}
                        className="cursor-pointer"
                      />
                      {advanceProof && (
                        <p className="text-xs text-green-600 mt-1">✓ {advanceProof.name}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Loan Contract Section removed - now handled via popup during Loan Agreement generation */}

              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium mb-3">Home Address</h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>State</Label>
                    <Input
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      placeholder="State/Province"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>District</Label>
                    <Input
                      value={formData.district}
                      onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                      placeholder="District"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Town/Village</Label>
                    <Input
                      value={formData.town_village}
                      onChange={(e) => setFormData({ ...formData, town_village: e.target.value })}
                      placeholder="Town or Village"
                    />
                  </div>
                </div>
                <div className="space-y-2 mt-4">
                  <Label>Full Home Address</Label>
                  <Textarea
                    value={formData.full_home_address}
                    onChange={(e) => setFormData({ ...formData, full_home_address: e.target.value })}
                    placeholder="Enter complete home address"
                    rows={2}
                  />
                </div>
              </div>

              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium mb-3">Home Country Driving License</h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>License Number</Label>
                    <Input
                      value={formData.home_country_dl_number}
                      onChange={(e) => setFormData({ ...formData, home_country_dl_number: e.target.value })}
                      placeholder="DL Number"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Issue Date</Label>
                    <Input
                      type="date"
                      value={formData.home_country_dl_issue_date}
                      onChange={(e) => setFormData({ ...formData, home_country_dl_issue_date: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Expiry Date</Label>
                    <Input
                      type="date"
                      value={formData.home_country_dl_expiry_date}
                      onChange={(e) => setFormData({ ...formData, home_country_dl_expiry_date: e.target.value })}
                    />
                  </div>
                </div>
              </div>
              
              {/* Advance Payment History (Edit Mode Only) */}
              {editMode && selectedCandidate && (
                <div className="border-t pt-4 mt-4">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <History className="w-4 h-4" />
                    Advance Payment History
                  </h4>
                  
                  {/* Existing Payments */}
                  {selectedCandidate.advance_payments && selectedCandidate.advance_payments.length > 0 ? (
                    <div className="space-y-2 mb-4">
                      {selectedCandidate.advance_payments.map((payment, idx) => (
                        <div key={payment.id || idx} className="p-3 bg-slate-50 rounded-lg border space-y-2">
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="font-medium">{payment.currency} {payment.amount}</span>
                              {payment.currency !== 'AED' && payment.amount_aed ? (
                                <span className="text-sm text-green-600 ml-2">(AED {payment.amount_aed})</span>
                              ) : null}
                              <span className="text-sm text-slate-500 ml-2">
                                • {formatDateDisplay(payment.added_at)} by {payment.added_by}
                              </span>
                            </div>
                            <div className="flex gap-2 items-center flex-wrap">
                              {payment.currency !== 'AED' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => { setEditingAdvanceIdx(idx); setEditingAdvanceAed(payment.amount_aed || 0); }}
                                  className="gap-1 text-xs h-7 px-2"
                                  data-testid={`edit-advance-aed-${idx}`}
                                >
                                  <Edit className="w-3 h-3" />
                                  {payment.amount_aed ? 'Edit AED' : 'Add AED'}
                                </Button>
                              )}
                              {canEditAdvancePayments && editingRowIdx !== idx && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => startEditingAdvanceRow(idx, payment)}
                                  className="gap-1 text-xs h-7 px-2 border-blue-300 text-blue-700 hover:bg-blue-50"
                                  data-testid={`edit-advance-row-${idx}`}
                                  title="Edit currency / amount"
                                >
                                  <Edit className="w-3 h-3" /> Edit
                                </Button>
                              )}
                              {canDeleteAdvancePayments && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDeleteAdvancePayment(idx)}
                                  disabled={deletingRowIdx === idx}
                                  className="gap-1 text-xs h-7 px-2 border-red-300 text-red-700 hover:bg-red-50"
                                  data-testid={`delete-advance-row-${idx}`}
                                  title="Delete this advance payment"
                                >
                                  {deletingRowIdx === idx
                                    ? <Loader2 className="w-3 h-3 animate-spin" />
                                    : <Trash2 className="w-3 h-3" />}
                                </Button>
                              )}
                              {payment.proof ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => viewDocument(selectedCandidate.id, idx, 'advance-payment')}
                                  className="gap-1 text-blue-600 border-blue-300 hover:bg-blue-50"
                                >
                                  <Eye className="w-3 h-3" />
                                  View Proof
                                </Button>
                              ) : (
                                <div className="flex items-center gap-2">
                                  <Input
                                    id={`upload-advance-proof-${idx}`}
                                    type="file"
                                    accept=".jpg,.jpeg,.png,.pdf"
                                    className="hidden"
                                    disabled={uploadingProofIdx === idx}
                                    onChange={(e) => {
                                      const f = e.target.files?.[0];
                                      if (f) handleUploadAdvanceProof(idx, f);
                                      e.target.value = '';
                                    }}
                                    data-testid={`upload-advance-proof-input-${idx}`}
                                  />
                                  <Label
                                    htmlFor={`upload-advance-proof-${idx}`}
                                    className={`inline-flex items-center gap-1 text-xs h-7 px-2 rounded-md border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 cursor-pointer ${uploadingProofIdx === idx ? 'opacity-50 pointer-events-none' : ''}`}
                                    data-testid={`upload-advance-proof-btn-${idx}`}
                                  >
                                    {uploadingProofIdx === idx ? (
                                      <><Loader2 className="w-3 h-3 animate-spin" /> Uploading…</>
                                    ) : (
                                      <><Upload className="w-3 h-3" /> Upload Proof</>
                                    )}
                                  </Label>
                                </div>
                              )}
                            </div>
                          </div>
                          {editingAdvanceIdx === idx && (
                            <div className="flex items-center gap-2 pt-2 border-t">
                              <Label className="text-xs whitespace-nowrap">Equivalent AED:</Label>
                              <Input
                                type="number"
                                className="h-8 w-32"
                                value={editingAdvanceAed}
                                onChange={(e) => setEditingAdvanceAed(parseFloat(e.target.value) || 0)}
                                placeholder="AED amount"
                              />
                              <Button size="sm" className="h-8" onClick={() => handleUpdateAdvanceAed(idx)}>Save</Button>
                              <Button size="sm" variant="ghost" className="h-8" onClick={() => setEditingAdvanceIdx(null)}>Cancel</Button>
                            </div>
                          )}
                          {editingRowIdx === idx && (
                            <div className="pt-2 border-t space-y-2 bg-blue-50/50 -mx-3 -mb-3 px-3 pb-3 rounded-b-lg">
                              <p className="text-xs font-medium text-blue-800">Edit advance payment</p>
                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                <div>
                                  <Label className="text-xs">Currency</Label>
                                  <Select
                                    value={editingRow.currency}
                                    onValueChange={(v) => setEditingRow((r) => ({ ...r, currency: v }))}
                                  >
                                    <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="AED">AED</SelectItem>
                                      <SelectItem value="USD">USD</SelectItem>
                                      <SelectItem value="EUR">EUR</SelectItem>
                                      <SelectItem value="GBP">GBP</SelectItem>
                                      <SelectItem value="INR">INR</SelectItem>
                                      <SelectItem value="PKR">PKR</SelectItem>
                                      <SelectItem value="BDT">BDT</SelectItem>
                                      <SelectItem value="NPR">NPR</SelectItem>
                                      <SelectItem value="LKR">LKR</SelectItem>
                                      <SelectItem value="PHP">PHP</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div>
                                  <Label className="text-xs">Amount ({editingRow.currency})</Label>
                                  <Input
                                    type="number"
                                    className="h-8"
                                    value={editingRow.amount}
                                    onChange={(e) => setEditingRow((r) => ({ ...r, amount: parseFloat(e.target.value) || 0 }))}
                                  />
                                </div>
                                {editingRow.currency !== 'AED' && (
                                  <div>
                                    <Label className="text-xs">Equivalent AED</Label>
                                    <Input
                                      type="number"
                                      className="h-8"
                                      value={editingRow.amount_aed}
                                      onChange={(e) => setEditingRow((r) => ({ ...r, amount_aed: parseFloat(e.target.value) || 0 }))}
                                    />
                                  </div>
                                )}
                              </div>
                              <div className="flex gap-2">
                                <Button size="sm" className="h-8" onClick={() => handleSaveAdvanceRow(idx)} disabled={savingRow} data-testid={`save-advance-row-${idx}`}>
                                  {savingRow ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                                  Save
                                </Button>
                                <Button size="sm" variant="ghost" className="h-8" onClick={cancelEditingAdvanceRow}>Cancel</Button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 mb-4">No advance payments recorded</p>
                  )}
                  
                  {/* Add New Advance */}
                  <div className="p-4 border rounded-lg bg-blue-50 border-blue-200">
                    <h5 className="text-sm font-medium mb-3 text-blue-700">Add New Advance Payment</h5>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Currency</Label>
                        <Select
                          value={newAdvance.currency}
                          onValueChange={(value) => setNewAdvance({ ...newAdvance, currency: value })}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {CURRENCIES.map(({ code }) => (
                              <SelectItem key={code} value={code}>{code}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Amount</Label>
                        <Input
                          type="number"
                          className="h-9"
                          value={newAdvance.amount}
                          onChange={(e) => setNewAdvance({ ...newAdvance, amount: parseFloat(e.target.value) || 0 })}
                          placeholder="0"
                        />
                      </div>
                      {newAdvance.currency && newAdvance.currency !== 'AED' && (
                        <div className="space-y-1">
                          <Label className="text-xs">Equivalent AED <span className="text-red-500">*</span></Label>
                          <Input
                            type="number"
                            className="h-9"
                            data-testid="new-advance-aed"
                            required
                            value={newAdvance.amount_aed}
                            onChange={(e) => setNewAdvance({ ...newAdvance, amount_aed: parseFloat(e.target.value) || 0 })}
                            placeholder="0"
                          />
                        </div>
                      )}
                      <div className="space-y-1">
                        <Label className="text-xs">Proof</Label>
                        <Input
                          type="file"
                          accept=".jpg,.jpeg,.png,.pdf"
                          className="h-9 cursor-pointer"
                          onChange={(e) => setNewAdvance({ ...newAdvance, proof: e.target.files?.[0] || null })}
                        />
                      </div>
                      <div className="flex items-end">
                        <Button
                          size="sm"
                          onClick={handleAddAdvance}
                          disabled={addingAdvance || newAdvance.amount <= 0}
                          className="w-full h-9"
                        >
                          {addingAdvance ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
                          Add
                        </Button>
                      </div>
                    </div>
                    {newAdvance.proof && (
                      <p className="text-xs text-green-600 mt-2">✓ {newAdvance.proof.name}</p>
                    )}
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="references" className="space-y-4 mt-4">
              <div className="border rounded-lg p-4">
                <h4 className="font-medium mb-3">Reference 1</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Name</Label>
                    <Input
                      value={formData.reference_1_name}
                      onChange={(e) => setFormData({ ...formData, reference_1_name: e.target.value })}
                      placeholder="Reference name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Mobile</Label>
                    <Input
                      value={formData.reference_1_mobile}
                      onChange={(e) => setFormData({ ...formData, reference_1_mobile: e.target.value })}
                      placeholder="Mobile number"
                    />
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label>Address</Label>
                    <Textarea
                      value={formData.reference_1_address}
                      onChange={(e) => setFormData({ ...formData, reference_1_address: e.target.value })}
                      placeholder="Full address"
                      rows={2}
                    />
                  </div>
                </div>
              </div>

              <div className="border rounded-lg p-4">
                <h4 className="font-medium mb-3">Reference 2</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Name</Label>
                    <Input
                      value={formData.reference_2_name}
                      onChange={(e) => setFormData({ ...formData, reference_2_name: e.target.value })}
                      placeholder="Reference name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Mobile</Label>
                    <Input
                      value={formData.reference_2_mobile}
                      onChange={(e) => setFormData({ ...formData, reference_2_mobile: e.target.value })}
                      placeholder="Mobile number"
                    />
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label>Address</Label>
                    <Textarea
                      value={formData.reference_2_address}
                      onChange={(e) => setFormData({ ...formData, reference_2_address: e.target.value })}
                      placeholder="Full address"
                      rows={2}
                    />
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="documents" className="space-y-6 mt-4">
              {/* File format notice */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
                <strong>Supported formats:</strong> JPEG, PNG, PDF • <strong>Max size:</strong> 10 MB per file
              </div>
              
              {/* Personal Documents - Uploaded by User */}
              <div>
                <h4 className="font-medium mb-3 text-slate-700 border-b pb-2">Personal Documents</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { key: 'passport_front', label: 'Passport Front', icon: CreditCard },
                    { key: 'passport_back', label: 'Passport Back', icon: CreditCard },
                    { key: 'passport_cover', label: 'Passport Cover Page', icon: File },
                    { key: 'photo', label: 'Photo (Passport size, white BG)', icon: Image },
                    { key: 'driving_license', label: 'Home Country Driving License Front', icon: CreditCard },
                    { key: 'driving_license_back', label: 'Home Country Driving License Back', icon: CreditCard },
                    { key: 'id_card', label: 'Home Country ID Card Front', icon: CreditCard },
                    { key: 'id_card_back', label: 'Home Country ID Card Back', icon: CreditCard },
                    { key: 'signed_mol', label: 'Signed MOL', icon: FileText }
                  ].map(({ key, label, icon: Icon }) => (
                    <div key={key} className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Icon className="w-4 h-4" />
                        {label}
                      </Label>
                      <Input
                        type="file"
                        accept=".jpg,.jpeg,.png,.pdf"
                        onChange={(e) => setDocuments({ ...documents, [key]: e.target.files[0] })}
                      />
                      {documents[key] && (
                        <p className="text-xs text-green-600">Selected: {documents[key].name}</p>
                      )}
                      {editMode && selectedCandidate?.documents?.[key] && !documents[key] && (
                        <p className="text-xs text-blue-600">Current: {selectedCandidate.documents[key].original_name}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Additional Documents — multi-file, append-not-replace.
                    Persisted immediately via /additional-documents endpoint so
                    each row is individually viewable, downloadable, and
                    soft-archivable from Edit + the candidate details drawer. */}
              <div>
                <h4 className="font-medium mb-3 text-slate-700 border-b pb-2 flex items-center gap-2">
                  <File className="w-4 h-4" />
                  Additional Documents
                  <span className="text-[11px] font-normal text-slate-500">(multiple files — JPEG / PNG / PDF, 10 MB each)</span>
                </h4>

                {/* Existing additional docs (Edit mode) */}
                {editMode && selectedCandidate?.additional_documents?.filter(d => !d.archived).length > 0 && (
                  <div className="mb-3 space-y-1.5">
                    {selectedCandidate.additional_documents.filter(d => !d.archived).map((doc) => (
                      <div key={doc.id} className="flex items-center gap-2 p-2 bg-slate-50 border border-slate-200 rounded text-sm" data-testid={`additional-doc-${doc.id}`}>
                        <File className="w-4 h-4 text-slate-500 shrink-0" />
                        <span className="flex-1 truncate" title={doc.original_name}>{doc.original_name}</span>
                        <span className="text-xs text-slate-500 whitespace-nowrap">{Math.round((doc.size || 0) / 1024)} KB</span>
                        <Button type="button" size="sm" variant="ghost" className="h-7 px-2"
                          onClick={() => window.open(`${API}/hiring/candidates/${selectedCandidate.id}/additional-documents/${doc.id}/download?inline=true`, '_blank')}>
                          <Eye className="w-3.5 h-3.5" />
                        </Button>
                        <Button type="button" size="sm" variant="ghost" className="h-7 px-2"
                          onClick={() => window.open(`${API}/hiring/candidates/${selectedCandidate.id}/additional-documents/${doc.id}/download`, '_blank')}>
                          <Download className="w-3.5 h-3.5" />
                        </Button>
                        <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-rose-600 hover:bg-rose-50"
                          onClick={async () => {
                            if (!window.confirm(`Archive "${doc.original_name}"? (File is retained on OneDrive; you can restore via audit.)`)) return;
                            try {
                              await axios.post(`${API}/hiring/candidates/${selectedCandidate.id}/additional-documents/${doc.id}/archive`, {}, { headers: getAuthHeader() });
                              // Optimistically remove from local view
                              setSelectedCandidate({
                                ...selectedCandidate,
                                additional_documents: selectedCandidate.additional_documents.map(d => d.id === doc.id ? {...d, archived: true} : d)
                              });
                              toast.success('Archived');
                              fetchData();
                            } catch (err) {
                              toast.error(formatErrorMessage(err, 'Failed to archive'));
                            }
                          }}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Multi-file picker + queue */}
                <Input
                  type="file"
                  accept=".jpg,.jpeg,.png,.pdf"
                  multiple
                  data-testid="additional-docs-file-input"
                  onChange={(e) => setAdditionalDocsToUpload([...(additionalDocsToUpload || []), ...Array.from(e.target.files || [])])}
                />
                {additionalDocsToUpload.length > 0 && (
                  <div className="mt-2 space-y-1">
                    <div className="text-xs text-slate-500">{additionalDocsToUpload.length} file(s) queued:</div>
                    {additionalDocsToUpload.map((f, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-xs bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
                        <File className="w-3 h-3 text-emerald-700" />
                        <span className="flex-1 truncate">{f.name}</span>
                        <span className="text-slate-500">{Math.round(f.size / 1024)} KB</span>
                        <button type="button" className="text-rose-600 hover:text-rose-800"
                          onClick={() => setAdditionalDocsToUpload(additionalDocsToUpload.filter((_, i) => i !== idx))}>×</button>
                      </div>
                    ))}
                    {editMode && selectedCandidate && (
                      <Button type="button" size="sm" variant="outline" className="mt-1 gap-1" disabled={uploadingAdditional}
                        data-testid="upload-additional-docs-btn"
                        onClick={async () => {
                          setUploadingAdditional(true);
                          try {
                            const fd = new FormData();
                            additionalDocsToUpload.forEach(f => fd.append('files', f));
                            const res = await axios.post(
                              `${API}/hiring/candidates/${selectedCandidate.id}/additional-documents`,
                              fd,
                              { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
                            );
                            toast.success(`Uploaded ${res.data.uploaded} file(s)`);
                            setSelectedCandidate({ ...selectedCandidate, additional_documents: res.data.additional_documents });
                            setAdditionalDocsToUpload([]);
                            fetchData();
                          } catch (err) {
                            toast.error(formatErrorMessage(err, 'Failed to upload additional documents'));
                          } finally {
                            setUploadingAdditional(false);
                          }
                        }}>
                        <Upload className="w-3.5 h-3.5" />
                        {uploadingAdditional ? 'Uploading…' : `Upload ${additionalDocsToUpload.length} file(s) now`}
                      </Button>
                    )}
                    {!editMode && (
                      <div className="text-[11px] text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-1">
                        These files will upload automatically after you save the new candidate.
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Travel Documents - Uploaded by User */}
              <div>
                <h4 className="font-medium mb-3 text-slate-700 border-b pb-2">Travel Documents</h4>
                <p className="text-xs text-slate-500 mb-3">Upload air ticket details for candidate travel</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <FileUp className="w-4 h-4" />
                      Air Ticket
                    </Label>
                    <Input
                      type="file"
                      accept=".jpg,.jpeg,.png,.pdf"
                      onChange={(e) => setDocuments({ ...documents, air_ticket: e.target.files[0] })}
                    />
                    {documents.air_ticket && (
                      <p className="text-xs text-green-600">Selected: {documents.air_ticket.name}</p>
                    )}
                    {editMode && selectedCandidate?.documents?.air_ticket && !documents.air_ticket && (
                      <p className="text-xs text-blue-600">Current: {selectedCandidate.documents.air_ticket.original_name}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Visa Documents - Permission controlled */}
              {(isAdmin || canUploadVisa) && (
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-3 text-slate-700 border-b pb-2 flex items-center gap-2">
                    <Badge variant="outline" className="text-amber-600 border-amber-400">Visa Documents</Badge>
                    Visa Documents
                  </h4>
                  <p className="text-xs text-slate-500 mb-3">Upload visa-related documents</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <File className="w-4 h-4" />
                        MOL (Ministry of Labour)
                      </Label>
                      <Input
                        type="file"
                        accept=".jpg,.jpeg,.png,.pdf"
                        onChange={(e) => setDocuments({ ...documents, mol: e.target.files[0] })}
                      />
                      {documents.mol && (
                        <p className="text-xs text-green-600">Selected: {documents.mol.name}</p>
                      )}
                      {editMode && selectedCandidate?.documents?.mol && !documents.mol && (
                        <p className="text-xs text-blue-600">Current: {selectedCandidate.documents.mol.original_name}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <File className="w-4 h-4" />
                        Employment Visa
                      </Label>
                      <Input
                        type="file"
                        accept=".jpg,.jpeg,.png,.pdf"
                        onChange={(e) => setDocuments({ ...documents, employment_visa: e.target.files[0] })}
                      />
                      {documents.employment_visa && (
                        <p className="text-xs text-green-600">Selected: {documents.employment_visa.name}</p>
                      )}
                      {editMode && selectedCandidate?.documents?.employment_visa && !documents.employment_visa && (
                        <p className="text-xs text-blue-600">Current: {selectedCandidate.documents.employment_visa.original_name}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>

          {/* Navigation Buttons */}
          <DialogFooter className="mt-6">
            <div className="flex w-full justify-between">
              <div>
                {currentStep > 1 && (
                  <Button variant="outline" onClick={() => setCurrentStep(currentStep - 1)}>
                    ← Back
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => { setShowAddDialog(false); resetForm(); }}>
                  Cancel
                </Button>
                {currentStep < 3 ? (
                  <Button 
                    onClick={() => {
                      // Validate step 1 before proceeding
                      if (currentStep === 1 && (!formData.name || !formData.passport_number || !formData.nationality || !formData.date_of_birth)) {
                        toast.error('Please fill in all required fields (Name, DOB, Nationality, Passport)');
                        return;
                      }
                      setCurrentStep(currentStep + 1);
                    }}
                  >
                    Next →
                  </Button>
                ) : (
                  <Button onClick={handleSubmit} disabled={saving} className="gap-2">
                    {saving ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</>
                    ) : (
                      <><UserPlus className="w-4 h-4" /> {editMode ? 'Update' : 'Submit'}</>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Candidate Details Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Candidate Details</DialogTitle>
          </DialogHeader>

          {selectedCandidate && (
            <div className="space-y-5">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">{selectedCandidate.name}</h3>
                {getStatusBadge(selectedCandidate.status)}
              </div>

              {/* Prominent Reason Banner for Rejected or Sent for Edit candidates */}
              {selectedCandidate.status === 'rejected' && (selectedCandidate.reject_reason || selectedCandidate.remarks) && (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg" data-testid="reject-reason-banner">
                  <div className="flex items-start gap-3">
                    <XCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-red-800">Rejection Reason</h4>
                      <p className="text-red-700 mt-1">{selectedCandidate.reject_reason || selectedCandidate.remarks}</p>
                    </div>
                  </div>
                </div>
              )}
              
              {selectedCandidate.status === 'sent_for_edit' && (selectedCandidate.edit_reason || selectedCandidate.remarks) && (
                <div className="bg-orange-50 border-l-4 border-orange-500 p-4 rounded-r-lg" data-testid="edit-reason-banner">
                  <div className="flex items-start gap-3">
                    <MessageSquare className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-orange-800">Edit Required</h4>
                      <p className="text-orange-700 mt-1">{selectedCandidate.edit_reason || selectedCandidate.remarks}</p>
                      <p className="text-xs text-orange-500 mt-2">Please make the requested changes and resubmit for review.</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Personal Information */}
              <div>
                <h4 className="font-medium text-slate-700 mb-2 text-sm uppercase tracking-wider border-b pb-1">Personal Information</h4>
                <div className="grid grid-cols-3 gap-x-6 gap-y-2 text-sm">
                  <div><span className="text-slate-500">Full Name:</span> <span className="font-medium">{selectedCandidate.name}</span></div>
                  <div><span className="text-slate-500">Nationality:</span> <span className="font-medium">{selectedCandidate.nationality}</span></div>
                  <div><span className="text-slate-500">Date of Birth:</span> <span className="font-medium">{formatDateDisplay(selectedCandidate.date_of_birth)}</span></div>
                  <div><span className="text-slate-500">Age:</span> <span className="font-medium">{selectedCandidate.age} years</span></div>
                  <div><span className="text-slate-500">Mobile:</span> <span className="font-medium">{selectedCandidate.country_code} {selectedCandidate.mobile_no}</span></div>
                  <div><span className="text-slate-500">State:</span> <span className="font-medium">{selectedCandidate.state || '-'}</span></div>
                  <div><span className="text-slate-500">District:</span> <span className="font-medium">{selectedCandidate.district || '-'}</span></div>
                  <div><span className="text-slate-500">Town/Village:</span> <span className="font-medium">{selectedCandidate.town_village || '-'}</span></div>
                  <div className="col-span-3"><span className="text-slate-500">Full Home Address:</span> <span className="font-medium">{selectedCandidate.full_home_address || '-'}</span></div>
                </div>
              </div>

              {/* Passport Details */}
              <div>
                <h4 className="font-medium text-slate-700 mb-2 text-sm uppercase tracking-wider border-b pb-1">Passport Details</h4>
                <div className="grid grid-cols-3 gap-x-6 gap-y-2 text-sm">
                  <div><span className="text-slate-500">Passport No.:</span> <span className="font-medium">{selectedCandidate.passport_number}</span></div>
                  <div><span className="text-slate-500">Issue Date:</span> <span className="font-medium">{formatDateDisplay(selectedCandidate.passport_issue_date) || '-'}</span></div>
                  <div><span className="text-slate-500">Expiry Date:</span> <span className="font-medium">{formatDateDisplay(selectedCandidate.passport_expiry_date) || '-'}</span></div>
                </div>
              </div>

              {/* Home Country DL Details */}
              <div>
                <h4 className="font-medium text-slate-700 mb-2 text-sm uppercase tracking-wider border-b pb-1">Home Country Driving License</h4>
                <div className="grid grid-cols-3 gap-x-6 gap-y-2 text-sm">
                  <div><span className="text-slate-500">DL Number:</span> <span className="font-medium">{selectedCandidate.home_country_dl_number || '-'}</span></div>
                  <div><span className="text-slate-500">Issue Date:</span> <span className="font-medium">{formatDateDisplay(selectedCandidate.home_country_dl_issue_date) || '-'}</span></div>
                  <div><span className="text-slate-500">Expiry Date:</span> <span className="font-medium">{formatDateDisplay(selectedCandidate.home_country_dl_expiry_date) || '-'}</span></div>
                </div>
              </div>

              {/* Financial Info */}
              <div>
                <h4 className="font-medium text-slate-700 mb-2 text-sm uppercase tracking-wider border-b pb-1">Financial Information</h4>
                <div className="grid grid-cols-3 gap-x-6 gap-y-2 text-sm">
                  <div><span className="text-slate-500">Advance Paid:</span> <span className="font-medium">{selectedCandidate.advance_currency || 'AED'} {selectedCandidate.advance_paid || 0}{selectedCandidate.advance_currency && selectedCandidate.advance_currency !== 'AED' && selectedCandidate.advance_paid_aed ? ` (AED ${selectedCandidate.advance_paid_aed})` : ''}</span></div>
                  {canViewLoanContract && (
                    <>
                      <div data-testid="loan-contract-display"><span className="text-slate-500">Contract Loan:</span> <span className="font-medium">AED {selectedCandidate.loan_contract_amount || 0}</span></div>
                      <div data-testid="installments-display"><span className="text-slate-500">Installments:</span> <span className="font-medium">{selectedCandidate.no_of_installments || '-'}</span></div>
                    </>
                  )}
                  <div><span className="text-slate-500">Lessee Company:</span> <span className="font-medium">{selectedCandidate.lessee_company_name || '-'}</span></div>
                </div>
              </div>

              {/* References */}
              <div>
                <h4 className="font-medium text-slate-700 mb-2 text-sm uppercase tracking-wider border-b pb-1">References</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <p className="text-xs font-semibold text-slate-500 mb-1">Reference 1</p>
                    <div className="space-y-1 text-sm">
                      <div><span className="text-slate-500">Name:</span> <span className="font-medium">{selectedCandidate.reference_1?.name || '-'}</span></div>
                      <div><span className="text-slate-500">Mobile:</span> <span className="font-medium">{selectedCandidate.reference_1?.mobile || '-'}</span></div>
                      <div><span className="text-slate-500">Address:</span> <span className="font-medium">{selectedCandidate.reference_1?.address || '-'}</span></div>
                    </div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <p className="text-xs font-semibold text-slate-500 mb-1">Reference 2</p>
                    <div className="space-y-1 text-sm">
                      <div><span className="text-slate-500">Name:</span> <span className="font-medium">{selectedCandidate.reference_2?.name || '-'}</span></div>
                      <div><span className="text-slate-500">Mobile:</span> <span className="font-medium">{selectedCandidate.reference_2?.mobile || '-'}</span></div>
                      <div><span className="text-slate-500">Address:</span> <span className="font-medium">{selectedCandidate.reference_2?.address || '-'}</span></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Remarks */}
              {selectedCandidate.remarks && selectedCandidate.status !== 'sent_for_edit' && selectedCandidate.status !== 'rejected' && (
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm font-medium text-slate-700">Admin Remarks:</p>
                  <p className="text-sm text-slate-600">{selectedCandidate.remarks}</p>
                </div>
              )}

              {/* Meta Info */}
              <div className="text-xs text-slate-400 flex gap-4 border-t pt-2">
                <span>Created: {formatDateDisplay(selectedCandidate.created_at)}</span>
                {selectedCandidate.created_by_name && <span>By: {selectedCandidate.created_by_name}</span>}
                {selectedCandidate.updated_at && <span>Updated: {formatDateDisplay(selectedCandidate.updated_at)}</span>}
              </div>

              {selectedCandidate.edit_history?.length > 0 && (
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <History className="w-4 h-4" /> Edit History
                  </h4>
                  <div className="space-y-3 max-h-60 overflow-y-auto">
                    {selectedCandidate.edit_history.map((entry, idx) => (
                      <div key={idx} className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                        <div className="text-xs text-slate-500 font-medium mb-2">
                          {formatDateDisplay(entry.changed_at)}
                          {entry.changed_by && ` • by ${entry.changed_by}`}
                        </div>
                        <ul className="space-y-1">
                          {entry.changes?.map((c, cIdx) => {
                            const formatValue = (val) => {
                              if (val === null || val === undefined) return '-';
                              if (typeof val === 'object') {
                                if (val.name !== undefined) return val.name || '-';
                                return 'updated';
                              }
                              return String(val) || '-';
                            };
                            const fieldDisplayNames = {
                              'name': 'Name', 'date_of_birth': 'Date of Birth', 'nationality': 'Nationality',
                              'passport_number': 'Passport Number', 'passport_issue_date': 'Passport Issue Date',
                              'passport_expiry_date': 'Passport Expiry Date', 'mobile_no': 'Mobile Number',
                              'country_code': 'Country Code', 'state': 'State', 'district': 'District',
                              'town_village': 'Town/Village', 'full_home_address': 'Full Address',
                              'reference_1': 'Reference 1', 'reference_2': 'Reference 2',
                              'advance_paid': 'Advance Paid', 'advance_currency': 'Currency',
                              'status': 'Status', 'remarks': 'Remarks', 'advance_payment_added': 'Advance Payment Added'
                            };
                            let fieldName = c.field;
                            if (fieldName.startsWith('documents.')) {
                              const docType = fieldName.replace('documents.', '').replace('_', ' ');
                              fieldName = `Document: ${docType.charAt(0).toUpperCase() + docType.slice(1)}`;
                            } else {
                              fieldName = fieldDisplayNames[fieldName] || fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                            }
                            return (
                              <li key={cIdx} className="text-xs flex items-start gap-1">
                                <span className="text-slate-400">•</span>
                                <span>
                                  <span className="font-medium text-slate-700">{fieldName}:</span>{' '}
                                  <span className="text-red-500 line-through">{formatValue(c.old)}</span>
                                  {' → '}
                                  <span className="text-green-600">{formatValue(c.new)}</span>
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowViewDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Documents Dialog */}
      <Dialog open={showDocumentsDialog} onOpenChange={setShowDocumentsDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Documents - {selectedCandidate?.name}</DialogTitle>
            <DialogDescription>View all documents for this candidate</DialogDescription>
          </DialogHeader>

          {selectedCandidate && (
            <Tabs defaultValue="uploaded" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="uploaded">Uploaded</TabsTrigger>
                <TabsTrigger value="generated">
                  Generated
                  {selectedCandidate.generated_documents && Object.keys(selectedCandidate.generated_documents).length > 0 && (
                    <Badge className="ml-1 bg-green-100 text-green-700 text-xs">{Object.keys(selectedCandidate.generated_documents).length}</Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="visa">Visa</TabsTrigger>
                <TabsTrigger value="travel">Travel</TabsTrigger>
              </TabsList>
              
              {/* Uploaded Documents Tab */}
              <TabsContent value="uploaded" className="mt-4">
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { key: 'passport_front', label: 'Passport Front' },
                    { key: 'passport_back', label: 'Passport Back' },
                    { key: 'passport_cover', label: 'Passport Cover' },
                    { key: 'photo', label: 'Photo' },
                    { key: 'driving_license', label: 'Driving License Front' },
                    { key: 'driving_license_back', label: 'Driving License Back' },
                    { key: 'id_card', label: 'ID Card Front' },
                    { key: 'id_card_back', label: 'ID Card Back' },
                    { key: 'signed_mol', label: 'Signed MOL' },
                    { key: 'signed_offer_letter', label: 'Signed Offer Letter' },
                    { key: 'signed_employment_agreement', label: 'Signed Employment Agreement' },
                    { key: 'signed_loan_agreement', label: 'Signed Loan Agreement' },
                    { key: 'candidate_video', label: 'Candidate Video', isVideo: true }
                  ].map(({ key, label, isVideo }) => {
                    const doc = selectedCandidate.documents?.[key];
                    const hasFile = doc && (doc.local_path || doc.onedrive_url || doc.onedrive_file_id);
                    const acceptTypes = isVideo ? ".mp4,.mov,.avi,.webm" : ".pdf,.jpg,.jpeg,.png";
                    return (
                      <div key={key} className={`p-3 border rounded-lg ${isVideo ? 'col-span-2 bg-purple-50 border-purple-200' : ''}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="font-medium text-sm">{label}</div>
                          {isVideo && <span className="text-xs text-purple-600">Max 20MB</span>}
                        </div>
                        {hasFile ? (
                          <>
                            <div className="flex gap-2">
                              {(isAdmin || canViewUploaded) && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="flex-1 gap-1"
                                  onClick={() => viewDocument(selectedCandidate.id, key, 'documents')}
                                >
                                  <Eye className="w-3 h-3" />
                                  View
                                </Button>
                              )}
                              {(isAdmin || canDownloadUploaded) && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="gap-1"
                                  onClick={() => window.open(`${API}/hiring/candidates/${selectedCandidate.id}/documents/${key}/download`, '_blank')}
                                >
                                  <Download className="w-3 h-3" />
                                </Button>
                              )}
                            </div>
                            <p className="text-xs text-slate-500 mt-2 truncate" title={selectedCandidate.documents[key]?.original_name}>
                              {selectedCandidate.documents[key]?.original_name || 'Document'}
                            </p>
                            {/* Replace button */}
                            {(isAdmin || canReplaceUploaded) && (
                              <div className="mt-2">
                                <input
                                  type="file"
                                  id={`uploaded-replace-${key}`}
                                  className="hidden"
                                  accept={acceptTypes}
                                  onChange={async (e) => {
                                    const file = e.target.files[0];
                                    if (!file) return;
                                    if (isVideo && file.size > 20 * 1024 * 1024) {
                                      toast.error('Video file must be under 20MB');
                                      return;
                                    }
                                    const formData = new FormData();
                                    formData.append(key, file);
                                    try {
                                      await axios.post(`${API}/hiring/candidates/${selectedCandidate.id}/documents`, formData, {
                                        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
                                      });
                                      toast.success(`${label} updated`);
                                      fetchData();
                                    } catch (error) {
                                      toast.error(formatErrorMessage(error, 'Failed to upload'));
                                    }
                                  }}
                                />
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="w-full text-xs"
                                  onClick={() => document.getElementById(`uploaded-replace-${key}`).click()}
                                >
                                  <Upload className="w-3 h-3 mr-1" />
                                  Replace
                                </Button>
                              </div>
                            )}
                          </>
                        ) : (
                          <div>
                            {(isAdmin || canUploadUploaded) ? (
                              <>
                                <input
                                  type="file"
                                  id={`uploaded-upload-${key}`}
                                  className="hidden"
                                  accept={acceptTypes}
                                  onChange={async (e) => {
                                    const file = e.target.files[0];
                                    if (!file) return;
                                    if (isVideo && file.size > 20 * 1024 * 1024) {
                                      toast.error('Video file must be under 20MB');
                                      return;
                                    }
                                    const formData = new FormData();
                                    formData.append(key, file);
                                    try {
                                      await axios.post(`${API}/hiring/candidates/${selectedCandidate.id}/documents`, formData, {
                                        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
                                      });
                                      toast.success(`${label} uploaded`);
                                      fetchData();
                                    } catch (error) {
                                      toast.error(formatErrorMessage(error, 'Failed to upload'));
                                    }
                                  }}
                                />
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="w-full gap-1"
                                  onClick={() => document.getElementById(`uploaded-upload-${key}`).click()}
                                >
                                  <Upload className="w-3 h-3" />
                                  Upload
                                </Button>
                              </>
                            ) : (
                              <p className="text-xs text-slate-400 italic">Not uploaded</p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </TabsContent>

              {/* Additional Documents — read-only slice for the details drawer.
                   Full CRUD (upload/archive) lives in the Edit dialog. */}
              {selectedCandidate.additional_documents?.filter(d => !d.archived).length > 0 && (
                <TabsContent value="uploaded" className="mt-4">
                  <div className="mt-6 pt-4 border-t">
                    <h4 className="font-medium mb-3 text-slate-700 flex items-center gap-2">
                      <File className="w-4 h-4" />
                      Additional Documents
                      <span className="text-xs font-normal text-slate-500">({selectedCandidate.additional_documents.filter(d => !d.archived).length})</span>
                    </h4>
                    <div className="space-y-1.5">
                      {selectedCandidate.additional_documents.filter(d => !d.archived).map((doc) => (
                        <div key={doc.id} className="flex items-center gap-2 p-2 bg-slate-50 border border-slate-200 rounded text-sm">
                          <File className="w-4 h-4 text-slate-500 shrink-0" />
                          <span className="flex-1 truncate" title={doc.original_name}>{doc.original_name}</span>
                          <span className="text-xs text-slate-500 whitespace-nowrap">{Math.round((doc.size || 0) / 1024)} KB</span>
                          {(isAdmin || canViewUploaded) && (
                            <Button size="sm" variant="outline" className="h-7 px-2 gap-1"
                              onClick={() => window.open(`${API}/hiring/candidates/${selectedCandidate.id}/additional-documents/${doc.id}/download?inline=true`, '_blank')}>
                              <Eye className="w-3 h-3" />
                            </Button>
                          )}
                          {(isAdmin || canDownloadUploaded) && (
                            <Button size="sm" variant="outline" className="h-7 px-2 gap-1"
                              onClick={() => window.open(`${API}/hiring/candidates/${selectedCandidate.id}/additional-documents/${doc.id}/download`, '_blank')}>
                              <Download className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </TabsContent>
              )}
              <TabsContent value="generated" className="mt-4">
                {selectedCandidate.generated_documents && Object.keys(selectedCandidate.generated_documents).length > 0 ? (
                  <div className="space-y-3">
                    {Object.entries(selectedCandidate.generated_documents).map(([type, docInfo]) => {
                      const downloadUrl = `${API}/hiring/candidates/${selectedCandidate.id}/generated/${type}/download?download=true`;
                      return (
                        <div key={type} className="p-4 border rounded-lg bg-gradient-to-r from-green-50 to-emerald-50">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-medium capitalize">{type.replace(/_/g, ' ')}</div>
                              <div className="text-xs text-slate-500 mt-1">
                                Generated: {docInfo.generated_at ? formatDateDisplay(docInfo.generated_at) : 'N/A'}
                              </div>
                            </div>
                            <div className="flex gap-2">
                              {(isAdmin || canViewGenerated) && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="gap-2"
                                  onClick={() => window.open(`/pdf-viewer?candidateId=${selectedCandidate.id}&docType=${type}&name=${encodeURIComponent(type.replace(/_/g, ' '))}`, '_blank')}
                                  data-testid={`view-generated-${type}`}
                                >
                                  <Eye className="w-3 h-3" />
                                  View
                                </Button>
                              )}
                              {(isAdmin || canDownloadGenerated) && (
                                <Button
                                  size="sm"
                                  className="gap-2 bg-green-600 hover:bg-green-700"
                                  onClick={() => window.open(downloadUrl, '_blank')}
                                  data-testid={`download-generated-${type}`}
                                >
                                  <Download className="w-3 h-3" />
                                  Download
                                </Button>
                              )}
                              {(isAdmin || canDownloadGenerated) && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="gap-2 border-blue-300 text-blue-600 hover:bg-blue-50"
                                  onClick={() => handleEmailDocument(selectedCandidate, type, 'generated', type.replace(/_/g, ' '))}
                                  data-testid={`email-generated-${type}`}
                                  title={selectedCandidate.email ? `Send to ${selectedCandidate.email}` : 'Candidate has no email — edit candidate first'}
                                >
                                  <Mail className="w-3 h-3" />
                                  Email
                                </Button>
                              )}
                              {(isAdmin || canDownloadGenerated) && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="gap-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                  onClick={() => handleWhatsAppDocument(selectedCandidate, type, type.replace(/_/g, ' '), 'generated')}
                                  data-testid={`whatsapp-generated-${type}`}
                                  title={selectedCandidate.mobile_no ? `Send to ${selectedCandidate.country_code || ''}${selectedCandidate.mobile_no}` : 'Candidate has no mobile — edit candidate first'}
                                  disabled={!selectedCandidate.mobile_no}
                                >
                                  <WhatsAppIcon className="w-3 h-3 text-[#25D366]" />
                                  WhatsApp
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No documents generated yet</p>
                    <p className="text-sm mt-1">Use "Generate Documents" to create offer letters and agreements</p>
                  </div>
                )}
              </TabsContent>

              {/* Visa Documents Tab */}
              <TabsContent value="visa" className="mt-4">
                <div className="grid grid-cols-1 gap-4">
                  {[
                    { key: 'mol', label: 'MOL (Ministry of Labour)', dateKey: 'mol_issue_date' },
                    { key: 'employment_visa', label: 'Employment Visa', dateKey: 'employment_visa_issue_date' }
                  ].map(({ key, label, dateKey }) => {
                    const doc = selectedCandidate.documents?.[key];
                    const issueDate = selectedCandidate.documents?.[dateKey] || selectedCandidate[dateKey] || '';
                    const canUpload = isAdmin || canUploadVisa;
                    const canView = isAdmin || canViewVisa;
                    const canDownload = isAdmin || canDownloadVisa;
                    const canReplace = isAdmin || canReplaceVisa;
                    return (
                      <div key={key} className="p-4 border rounded-lg bg-amber-50 border-amber-200">
                        <div className="flex items-center justify-between mb-3">
                          <div className="font-medium text-sm">{label}</div>
                          {doc && (
                            <span className="text-xs text-green-600 flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3" />
                              Uploaded
                            </span>
                          )}
                        </div>
                        
                        {/* Document Issue Date Field */}
                        <div className="mb-3">
                          <label className="text-xs text-slate-600 block mb-1">Document Issue Date</label>
                          <input
                            type="date"
                            value={issueDate}
                            onChange={async (e) => {
                              try {
                                const formData = new FormData();
                                formData.append(dateKey, e.target.value);
                                await axios.post(`${API}/hiring/candidates/${selectedCandidate.id}/documents`, formData, {
                                  headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
                                });
                                fetchData();
                              } catch (error) {
                                toast.error('Failed to save date');
                              }
                            }}
                            className="w-full px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-amber-500"
                            disabled={!canUpload && !canReplace}
                          />
                        </div>
                        
                        {doc ? (
                          <>
                            <div className="flex gap-2">
                              {canView && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="flex-1 gap-1"
                                  onClick={() => viewDocument(selectedCandidate.id, key, 'documents')}
                                >
                                  <Eye className="w-3 h-3" />
                                  View
                                </Button>
                              )}
                              {canDownload && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="gap-1"
                                  onClick={() => window.open(`${API}/hiring/candidates/${selectedCandidate.id}/documents/${key}/download`, '_blank')}
                                >
                                  <Download className="w-3 h-3" />
                                </Button>
                              )}
                              {canDownload && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="gap-1 border-blue-300 text-blue-600 hover:bg-blue-50"
                                  onClick={() => handleEmailDocument(selectedCandidate, key, 'uploaded', label)}
                                  data-testid={`email-visa-${key}`}
                                  title={selectedCandidate.email ? `Send to ${selectedCandidate.email}` : 'Candidate has no email — edit candidate first'}
                                >
                                  <Mail className="w-3 h-3" />
                                  Email
                                </Button>
                              )}
                              {canDownload && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                  onClick={() => handleWhatsAppDocument(selectedCandidate, key, label)}
                                  data-testid={`whatsapp-visa-${key}`}
                                  title={selectedCandidate.mobile_no ? `Send to ${selectedCandidate.country_code || ''}${selectedCandidate.mobile_no}` : 'Candidate has no mobile — edit candidate first'}
                                  disabled={!selectedCandidate.mobile_no}
                                >
                                  <WhatsAppIcon className="w-3 h-3 text-[#25D366]" />
                                  WhatsApp
                                </Button>
                              )}
                            </div>
                            <p className="text-xs text-slate-500 mt-2 truncate" title={doc?.original_name}>
                              {doc?.original_name || 'Document'}
                            </p>
                            {/* Replace button */}
                            {canReplace && (
                              <div className="mt-2">
                                <input
                                  type="file"
                                  id={`visa-upload-${key}`}
                                  className="hidden"
                                  accept=".pdf,.jpg,.jpeg,.png"
                                  onChange={async (e) => {
                                    const file = e.target.files[0];
                                    if (!file) return;
                                    const formData = new FormData();
                                    formData.append(key, file);
                                    try {
                                      await axios.post(`${API}/hiring/candidates/${selectedCandidate.id}/documents`, formData, {
                                        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
                                      });
                                      toast.success(`${label} updated successfully`);
                                      fetchData();
                                    } catch (error) {
                                      toast.error(formatErrorMessage(error, 'Failed to upload'));
                                    }
                                  }}
                                />
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="w-full text-xs"
                                  onClick={() => document.getElementById(`visa-upload-${key}`).click()}
                                >
                                  <Upload className="w-3 h-3 mr-1" />
                                  Replace
                                </Button>
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="space-y-2">
                            {canUpload && (
                              <>
                                <input
                                  type="file"
                                  id={`visa-upload-${key}`}
                                  className="hidden"
                                  accept=".pdf,.jpg,.jpeg,.png"
                                  onChange={async (e) => {
                                    const file = e.target.files[0];
                                    if (!file) return;
                                    const formData = new FormData();
                                    formData.append(key, file);
                                    try {
                                      await axios.post(`${API}/hiring/candidates/${selectedCandidate.id}/documents`, formData, {
                                        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
                                      });
                                      toast.success(`${label} uploaded successfully`);
                                      fetchData();
                                    } catch (error) {
                                      toast.error(formatErrorMessage(error, 'Failed to upload'));
                                    }
                                  }}
                                />
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="w-full gap-1"
                                  onClick={() => document.getElementById(`visa-upload-${key}`).click()}
                                >
                                  <Upload className="w-3 h-3" />
                                  Upload
                                </Button>
                              </>
                            )}
                            {!canUpload && <p className="text-xs text-slate-400">Not uploaded</p>}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </TabsContent>

              {/* Travel Documents Tab */}
              <TabsContent value="travel" className="mt-4">
                <div className="space-y-4">
                  {/* Travel Info Fields */}
                  <div className="p-4 border rounded-lg bg-blue-50 border-blue-200">
                    <div className="font-medium text-sm mb-3">Flight Information</div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-slate-600 block mb-1">Airlines</label>
                        <input
                          type="text"
                          value={selectedCandidate.travel_airlines || ''}
                          onChange={async (e) => {
                            try {
                              const formData = new FormData();
                              formData.append('travel_airlines', e.target.value);
                              await axios.post(`${API}/hiring/candidates/${selectedCandidate.id}/documents`, formData, {
                                headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
                              });
                            } catch (error) {
                              toast.error('Failed to save');
                            }
                          }}
                          onBlur={fetchData}
                          className="w-full px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., Emirates, Air India"
                          disabled={!(isAdmin || canUploadTravel || canReplaceTravel)}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-600 block mb-1">Flight No.</label>
                        <input
                          type="text"
                          value={selectedCandidate.travel_flight_no || ''}
                          onChange={async (e) => {
                            try {
                              const formData = new FormData();
                              formData.append('travel_flight_no', e.target.value);
                              await axios.post(`${API}/hiring/candidates/${selectedCandidate.id}/documents`, formData, {
                                headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
                              });
                            } catch (error) {
                              toast.error('Failed to save');
                            }
                          }}
                          onBlur={fetchData}
                          className="w-full px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., EK501"
                          disabled={!(isAdmin || canUploadTravel || canReplaceTravel)}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-600 block mb-1">Date of Arrival</label>
                        <input
                          type="date"
                          value={selectedCandidate.travel_arrival_date || ''}
                          onChange={async (e) => {
                            try {
                              const formData = new FormData();
                              formData.append('travel_arrival_date', e.target.value);
                              await axios.post(`${API}/hiring/candidates/${selectedCandidate.id}/documents`, formData, {
                                headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
                              });
                              fetchData();
                            } catch (error) {
                              toast.error('Failed to save');
                            }
                          }}
                          className="w-full px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-blue-500"
                          disabled={!(isAdmin || canUploadTravel || canReplaceTravel)}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-600 block mb-1">Local Time of Arrival</label>
                        <input
                          type="time"
                          value={selectedCandidate.travel_arrival_time || ''}
                          onChange={async (e) => {
                            try {
                              const formData = new FormData();
                              formData.append('travel_arrival_time', e.target.value);
                              await axios.post(`${API}/hiring/candidates/${selectedCandidate.id}/documents`, formData, {
                                headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
                              });
                              fetchData();
                            } catch (error) {
                              toast.error('Failed to save');
                            }
                          }}
                          className="w-full px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-blue-500"
                          disabled={!(isAdmin || canUploadTravel || canReplaceTravel)}
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Air Ticket Upload */}
                  <div className="p-4 border rounded-lg bg-blue-50 border-blue-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="font-medium text-sm">Air Ticket</div>
                      {selectedCandidate.documents?.air_ticket && (
                        <span className="text-xs text-green-600 flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" />
                          Uploaded
                        </span>
                      )}
                    </div>
                    {selectedCandidate.documents?.air_ticket ? (
                      <>
                        <div className="flex gap-2">
                          {(isAdmin || canViewTravel) && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="flex-1 gap-1"
                              onClick={() => viewDocument(selectedCandidate.id, 'air_ticket', 'documents')}
                            >
                              <Eye className="w-3 h-3" />
                              View
                            </Button>
                          )}
                          {(isAdmin || canDownloadTravel) && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="gap-1"
                              onClick={() => window.open(`${API}/hiring/candidates/${selectedCandidate.id}/documents/air_ticket/download`, '_blank')}
                            >
                              <Download className="w-3 h-3" />
                            </Button>
                          )}
                          {(isAdmin || canDownloadTravel) && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                              onClick={() => handleWhatsAppDocument(selectedCandidate, 'air_ticket', 'Air Ticket')}
                              data-testid="whatsapp-travel-air_ticket"
                              title={selectedCandidate.mobile_no ? `Send to ${selectedCandidate.country_code || ''}${selectedCandidate.mobile_no}` : 'Candidate has no mobile — edit candidate first'}
                              disabled={!selectedCandidate.mobile_no}
                            >
                              <WhatsAppIcon className="w-3 h-3 text-[#25D366]" />
                              WhatsApp
                            </Button>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 mt-2 truncate">
                          {selectedCandidate.documents?.air_ticket?.original_name || 'Document'}
                        </p>
                        {(isAdmin || canReplaceTravel) && (
                          <div className="mt-2">
                            <input
                              type="file"
                              id="travel-upload-air_ticket"
                              className="hidden"
                              accept=".pdf,.jpg,.jpeg,.png"
                              onChange={async (e) => {
                                const file = e.target.files[0];
                                if (!file) return;
                                const formData = new FormData();
                                formData.append('air_ticket', file);
                                try {
                                  await axios.post(`${API}/hiring/candidates/${selectedCandidate.id}/documents`, formData, {
                                    headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
                                  });
                                  toast.success('Air Ticket updated');
                                  fetchData();
                                } catch (error) {
                                  toast.error(formatErrorMessage(error, 'Failed to upload'));
                                }
                              }}
                            />
                            <Button
                              size="sm"
                              variant="ghost"
                              className="w-full text-xs"
                              onClick={() => document.getElementById('travel-upload-air_ticket').click()}
                            >
                              <Upload className="w-3 h-3 mr-1" />
                              Replace
                            </Button>
                          </div>
                        )}
                      </>
                    ) : (
                      <div>
                        {(isAdmin || canUploadTravel) ? (
                          <>
                            <input
                              type="file"
                              id="travel-upload-air_ticket"
                              className="hidden"
                              accept=".pdf,.jpg,.jpeg,.png"
                              onChange={async (e) => {
                                const file = e.target.files[0];
                                if (!file) return;
                                const formData = new FormData();
                                formData.append('air_ticket', file);
                                try {
                                  await axios.post(`${API}/hiring/candidates/${selectedCandidate.id}/documents`, formData, {
                                    headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
                                  });
                                  toast.success('Air Ticket uploaded');
                                  fetchData();
                                } catch (error) {
                                  toast.error(formatErrorMessage(error, 'Failed to upload'));
                                }
                              }}
                            />
                            <Button
                              size="sm"
                              variant="outline"
                              className="w-full gap-1"
                              onClick={() => document.getElementById('travel-upload-air_ticket').click()}
                            >
                              <Upload className="w-3 h-3" />
                              Upload
                            </Button>
                          </>
                        ) : (
                          <p className="text-xs text-slate-400">Not uploaded</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDocumentsDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate Documents Dialog */}
      <Dialog open={showGenerateDocsDialog} onOpenChange={setShowGenerateDocsDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Generate Documents - {selectedCandidate?.name}</DialogTitle>
            <DialogDescription>Select documents to generate for this accepted candidate</DialogDescription>
          </DialogHeader>

          {selectedCandidate && (
            <div className="space-y-3">
              {[
                { type: 'offer_letter', label: 'Offer Letter' },
                { type: 'employment_agreement', label: 'Employment Agreement' },
                { type: 'loan_agreement', label: 'Loan Agreement' }
              ].map(({ type, label }) => {
                const generated = selectedCandidate.generated_documents?.[type];
                const signed = selectedCandidate.signed_documents?.[type];
                const tmplInfo = templateAvailability[type];
                const templateMissing = tmplInfo && !tmplInfo.available;
                
                return (
                  <div key={type} className={`p-4 border rounded-lg ${templateMissing ? 'opacity-60 bg-slate-50' : ''}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">{label}</span>
                      <div className="flex items-center gap-2">
                        {templateMissing && <Badge variant="outline" className="text-orange-500 text-xs">No Template</Badge>}
                        {generated && <Badge variant="outline" className="text-green-600">Generated</Badge>}
                      </div>
                    </div>
                    
                    {templateMissing && (
                      <p className="text-xs text-orange-600 mb-2">No active template configured for this document type. Please upload a template first.</p>
                    )}
                    
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleGenerateDocument(selectedCandidate.id, type)}
                        className="gap-1"
                        disabled={generatingDoc !== null || templateMissing}
                        data-testid={`generate-${type}-btn`}
                      >
                        {generatingDoc === type ? (
                          <>
                            <Loader2 className="w-3 h-3 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <FileText className="w-3 h-3" />
                            {generated ? 'Regenerate' : 'Generate'}
                          </>
                        )}
                      </Button>
                      
                      {generated && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => viewDocument(selectedCandidate.id, type, 'generated')}
                            className="gap-1"
                          >
                            <Download className="w-3 h-3" />
                            Download
                          </Button>
                          
                          <Label className="cursor-pointer">
                            <Input
                              type="file"
                              accept=".pdf"
                              className="hidden"
                              onChange={(e) => {
                                if (e.target.files[0]) {
                                  handleUploadSigned(selectedCandidate.id, type, e.target.files[0]);
                                }
                              }}
                            />
                            <Button size="sm" variant="outline" className="gap-1" asChild>
                              <span>
                                <Upload className="w-3 h-3" />
                                Upload Signed
                              </span>
                            </Button>
                          </Label>
                        </>
                      )}
                    </div>
                    
                    {signed && (
                      <div className="mt-2 text-xs text-green-600">
                        Signed document uploaded: {signed.original_name}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGenerateDocsDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog with Reasons */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Reject Candidate</DialogTitle>
            <DialogDescription>
              Select a reason for rejecting {selectedCandidate?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Reason for Rejection</label>
              <Select value={selectedReason} onValueChange={setSelectedReason}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a reason..." />
                </SelectTrigger>
                <SelectContent>
                  {REJECT_REASONS.map((reason, idx) => (
                    <SelectItem key={idx} value={reason}>{reason}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedReason === 'Other reason' && (
              <div>
                <label className="text-sm font-medium mb-2 block">Specify Reason</label>
                <Textarea
                  value={customReason}
                  onChange={(e) => setCustomReason(e.target.value)}
                  placeholder="Enter the reason for rejection..."
                  rows={3}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>Cancel</Button>
            <Button 
              variant="destructive"
              disabled={!selectedReason || (selectedReason === 'Other reason' && !customReason.trim())}
              onClick={async () => {
                const reason = selectedReason === 'Other reason' ? customReason : selectedReason;
                await handleStatusChange(selectedCandidate.id, 'rejected', reason);
                setShowRejectDialog(false);
              }}
            >
              Reject Candidate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send for Edit Dialog with Reasons */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-orange-600">Send for Edit</DialogTitle>
            <DialogDescription>
              Select a reason for sending {selectedCandidate?.name}'s application back for editing
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Reason for Edit</label>
              <Select value={selectedReason} onValueChange={setSelectedReason}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a reason..." />
                </SelectTrigger>
                <SelectContent>
                  {EDIT_REASONS.map((reason, idx) => (
                    <SelectItem key={idx} value={reason}>{reason}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedReason === 'Other reason' && (
              <div>
                <label className="text-sm font-medium mb-2 block">Specify Reason</label>
                <Textarea
                  value={customReason}
                  onChange={(e) => setCustomReason(e.target.value)}
                  placeholder="Enter the reason..."
                  rows={3}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button 
              className="bg-orange-600 hover:bg-orange-700"
              disabled={!selectedReason || (selectedReason === 'Other reason' && !customReason.trim())}
              onClick={async () => {
                const reason = selectedReason === 'Other reason' ? customReason : selectedReason;
                await handleStatusChange(selectedCandidate.id, 'sent_for_edit', reason);
                setShowEditDialog(false);
              }}
            >
              Send for Edit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Date & Time Info Dialog */}
      <Dialog open={showDateTimeDialog} onOpenChange={setShowDateTimeDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-indigo-600" />
              Date & Time Info
            </DialogTitle>
            <DialogDescription>
              {selectedCandidateDateTime?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-slate-50 p-3 rounded-lg border">
                <p className="text-xs text-slate-500 mb-1">MOL Date</p>
                <p className="font-medium text-sm">{selectedCandidateDateTime?.mol_issue_date || '-'}</p>
              </div>
              <div className="bg-slate-50 p-3 rounded-lg border">
                <p className="text-xs text-slate-500 mb-1">Visa Date</p>
                <p className="font-medium text-sm">{selectedCandidateDateTime?.employment_visa_issue_date || '-'}</p>
              </div>
              <div className="bg-slate-50 p-3 rounded-lg border">
                <p className="text-xs text-slate-500 mb-1">Travel Date</p>
                <p className="font-medium text-sm">{selectedCandidateDateTime?.travel_arrival_date || '-'}</p>
              </div>
              <div className="bg-slate-50 p-3 rounded-lg border">
                <p className="text-xs text-slate-500 mb-1">Arrival Time</p>
                <p className="font-medium text-sm">{selectedCandidateDateTime?.travel_arrival_time || '-'}</p>
              </div>
            </div>
            <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
              <p className="text-xs text-blue-600 mb-1">Airlines</p>
              <p className="font-medium text-sm text-blue-800">{selectedCandidateDateTime?.travel_airlines || '-'}</p>
              {selectedCandidateDateTime?.travel_flight_no && (
                <p className="text-xs text-blue-600 mt-1">Flight: {selectedCandidateDateTime.travel_flight_no}</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDateTimeDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Loan Contract Dialog - shown when generating Loan Agreement */}
      <Dialog open={showLoanContractDialog} onOpenChange={setShowLoanContractDialog}>
        <DialogContent className="max-w-md" data-testid="loan-contract-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Loan Contract Details
            </DialogTitle>
            <DialogDescription>
              Enter the loan contract details for {selectedCandidate?.name} before generating the Loan Agreement
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Currency</Label>
              <div className="flex items-center h-10 px-3 border rounded-md bg-slate-50 text-sm text-slate-700">
                AED - UAE Dirham
              </div>
            </div>
            <div className="space-y-2">
              <Label>Loan Contract Amount *</Label>
              <Input
                type="number"
                data-testid="loan-contract-amount-input"
                value={loanContractForm.loan_contract_amount}
                onChange={(e) => setLoanContractForm({ ...loanContractForm, loan_contract_amount: parseFloat(e.target.value) || 0 })}
                placeholder="0.00"
              />
            </div>
            <div className="space-y-2">
              <Label>No. of Installments</Label>
              <Input
                type="number"
                min="1"
                data-testid="no-of-installments-input"
                value={loanContractForm.no_of_installments}
                onChange={(e) => setLoanContractForm({ ...loanContractForm, no_of_installments: parseInt(e.target.value) || 0 })}
                placeholder="e.g. 12"
              />
            </div>
            <div className="space-y-2">
              <Label>Installment Amount (AED)</Label>
              <div className="flex items-center h-10 px-3 border rounded-md bg-slate-50 text-sm font-semibold text-blue-700" data-testid="installment-amount-display">
                {loanContractForm.loan_contract_amount > 0 && loanContractForm.no_of_installments > 0
                  ? (loanContractForm.loan_contract_amount / loanContractForm.no_of_installments).toFixed(2)
                  : '0.00'}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLoanContractDialog(false)}>Cancel</Button>
            <Button
              data-testid="generate-loan-agreement-confirm-btn"
              disabled={loanContractForm.loan_contract_amount <= 0 || loanContractForm.no_of_installments <= 0 || generatingDoc !== null}
              onClick={() => {
                setShowLoanContractDialog(false);
                handleGenerateDocument(selectedCandidate.id, 'loan_agreement', loanContractForm);
              }}
            >
              {generatingDoc === 'loan_agreement' ? (
                <><Loader2 className="w-3 h-3 animate-spin mr-1" /> Generating...</>
              ) : (
                <><FileText className="w-3 h-3 mr-1" /> Generate Loan Agreement</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Accept Candidate - Lessee Company Confirmation Dialog */}
      <Dialog open={showAcceptLesseeDialog} onOpenChange={setShowAcceptLesseeDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Lessee Company</DialogTitle>
            <DialogDescription>Select the in-house lessee company for this candidate before accepting.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Lessee Company <span className="text-red-500">*</span></Label>
              <Select value={selectedLesseeId} onValueChange={setSelectedLesseeId}>
                <SelectTrigger data-testid="accept-lessee-select">
                  <SelectValue placeholder="Select a lessee company" />
                </SelectTrigger>
                <SelectContent>
                  {inHouseLessees.length === 0 && (
                    <SelectItem value="__none" disabled>No in-house lessees available</SelectItem>
                  )}
                  {inHouseLessees.map(lessee => (
                    <SelectItem key={lessee.id} value={lessee.id}>
                      {lessee.company_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAcceptLesseeDialog(false)}>Cancel</Button>
            <Button
              data-testid="confirm-accept-btn"
              onClick={confirmAcceptWithLessee}
              disabled={!selectedLesseeId}
              className="bg-green-600 hover:bg-green-700"
            >
              <CheckCircle2 className="w-4 h-4 mr-1" />
              Accept Candidate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Lessee Company Dialog */}
      <Dialog open={showChangeLesseeDialog} onOpenChange={setShowChangeLesseeDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Change Lessee Company</DialogTitle>
            <DialogDescription>
              {selectedCandidate ? `Update lessee company for ${selectedCandidate.name}` : 'Select a new lessee company'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Current Lessee</Label>
              <div className="text-sm text-slate-600 p-2 bg-slate-50 rounded">
                {selectedCandidate?.lessee_company_name || 'Not assigned'}
              </div>
            </div>
            <div className="space-y-2">
              <Label>New Lessee Company <span className="text-red-500">*</span></Label>
              <Select value={selectedLesseeId} onValueChange={setSelectedLesseeId}>
                <SelectTrigger data-testid="change-lessee-select">
                  <SelectValue placeholder="Select a lessee company" />
                </SelectTrigger>
                <SelectContent>
                  {inHouseLessees.length === 0 && (
                    <SelectItem value="__none" disabled>No in-house lessees available</SelectItem>
                  )}
                  {inHouseLessees.map(lessee => (
                    <SelectItem key={lessee.id} value={lessee.id}>
                      {lessee.company_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowChangeLesseeDialog(false)}>Cancel</Button>
            <Button
              data-testid="confirm-change-lessee-btn"
              onClick={handleChangeLessee}
              disabled={!selectedLesseeId}
            >
              <Building2 className="w-4 h-4 mr-1" />
              Update Lessee
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Hiring;
